import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

// Retry wrapper for lazy imports — handles chunk load failures after deploys
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err) => {
      if (retries > 0) {
        // Wait briefly, then retry (chunk may have been updated on server)
        return new Promise<{ default: T }>((resolve) =>
          setTimeout(() => resolve(lazyRetry(factory, retries - 1) as any), 1500)
        );
      }
      // All retries exhausted — force reload to get fresh chunks
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
      }
      throw err;
    })
  );
}
// Clear chunk reload flag on successful load
if (sessionStorage.getItem('chunk_reload')) {
  sessionStorage.removeItem('chunk_reload');
}

import { UniversalKanban } from './components/UniversalKanban';
import { CRMApplicationDetail } from './components/CRMApplicationDetail';
const ProductModuleLayout = lazy(() => import('./components/product-modules/ProductModuleLayout'));

// URL-to-tab mapping for sidebar sub-routes (/crm/dashboard, /crm/rozliczenia, etc.)
const URL_TO_TAB: Record<string, string> = {
  dashboard: 'dashboard',
  rozliczenia: 'billing',
  raporty: 'reports',
  documents: 'documents',
  portfolio: 'portfolio',
  splaty: 'repayments',
  processMap: 'processMap',
};

function ProductModuleWithTab({ productCode }: { productCode: string }) {
  const { tab } = useParams<{ tab: string }>();
  const tabKey = tab ? (URL_TO_TAB[tab] || tab) : 'kanban';
  return (
    <Suspense fallback={<LazyFallback />}>
      <ProductModuleLayout productCode={productCode} initialTab={tabKey} />
    </Suspense>
  );
}

// Hutnik demo pages — lazy-loaded
const HutnikB2BDashboard = lazy(() => import('./pages/HutnikB2BDashboard'));
const StadiumVisionLanding = lazy(() => import('./pages/StadiumVisionLanding'));
const FanMemberCard = lazy(() => import('./pages/FanMemberCard'));

// Public forms — lazy-loaded (not needed for CRM operators)
const CRMApplicationForm = lazy(() => import('./components/CRMApplicationForm').then(m => ({ default: m.CRMApplicationForm })));
const WsparcieApplicationForm = lazy(() => import('./components/WsparcieApplicationForm').then(m => ({ default: m.WsparcieApplicationForm })));
const KFSApplicationForm = lazy(() => import('./components/KFSApplicationForm').then(m => ({ default: m.KFSApplicationForm })));
const ZUSApplicationForm = lazy(() => import('./components/ZUSApplicationForm').then(m => ({ default: m.ZUSApplicationForm })));
const BHPApplicationForm = lazy(() => import('./components/BHPApplicationForm').then(m => ({ default: m.BHPApplicationForm })));
const PozyczkaRozwojowaForm = lazy(() => import('./components/PozyczkaRozwojowaForm').then(m => ({ default: m.PozyczkaRozwojowaForm })));
const DotacjeRozwojoweForm = lazy(() => import('./components/DotacjeRozwojoweForm').then(m => ({ default: m.DotacjeRozwojoweForm })));
const CzystePowietrzeApplicationForm = lazy(() => import('./components/CzystePowietrzeApplicationForm').then(m => ({ default: m.CzystePowietrzeApplicationForm })));
const UslugaKsiegowaApplicationForm = lazy(() => import('./components/UslugaKsiegowaApplicationForm').then(m => ({ default: m.UslugaKsiegowaApplicationForm })));
const GenericApplicationForm = lazy(() => import('./components/GenericApplicationForm').then(m => ({ default: m.GenericApplicationForm })));
const SzkoleniaApplicationForm = lazy(() => import('./components/SzkoleniaApplicationForm').then(m => ({ default: m.SzkoleniaApplicationForm })));
const TurnusyApplicationForm = lazy(() => import('./components/TurnusyApplicationForm').then(m => ({ default: m.TurnusyApplicationForm })));
const WycieczkiForm = lazy(() => import('./components/WycieczkiForm').then(m => ({ default: m.WycieczkiForm })));
const Login = lazy(() => import('./components/Login').then(m => ({ default: m.Login })));
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Unauthorized } from './components/Unauthorized';
const ClientPortalPage = lazy(() => import('./pages/ClientPortalPage'));
const ClientPortalV2 = lazy(() => import('./pages/ClientPortalV2'));
const PortalLoginPage = lazy(() => import('./pages/PortalLoginPage'));
const ContractAcceptancePage = lazy(() => import('./pages/ContractAcceptancePage'));
const HostingRenewalPage = lazy(() => import('./pages/HostingRenewalPage'));
const HostingTrackerPage = lazy(() => import('./pages/HostingTrackerPage'));
import { AppLayout } from './components/AppLayout';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ui';
import { YearProvider } from './contexts/YearContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { ProductProvider } from './contexts/ProductContext';

// ─── Lazy-loaded modules ─────────────────────────────────────────────

const LazyFallback = () => (
  <div className="flex items-center justify-center py-24">
    <Loader2 size={32} className="animate-spin text-blue-500" />
  </div>
);

// Admin & tools
const AdminCMS = lazy(() => import('./pages/AdminCMS'));
const PrintQueuePage = lazy(() => import('./pages/PrintQueuePage'));
const SocialMediaPage = lazy(() => import('./pages/SocialMediaPage'));
const NewsletterDigestPage = lazy(() => import('./pages/NewsletterDigestPage'));
const VolunteerCertificatesPage = lazy(() => import('./pages/VolunteerCertificatesPage'));
const VolunteerRegistryPage = lazy(() => import('./pages/VolunteerRegistryPage'));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'));
const MailingPage = lazy(() => import('./pages/MailingPage'));
const MailingEditorPage = lazy(() => import('./pages/MailingEditorPage'));
const MailingDetailPage = lazy(() => import('./pages/MailingDetailPage'));
const MailMergePage = lazy(() => import('./pages/MailMergePage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const AIModulesPage = lazy(() => import('./pages/AIModulesPage'));
const ModuleBrowserPage = lazy(() => import('./pages/ModuleBrowserPage'));
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage'));
const ApiAnalyticsPage = lazy(() => import('./pages/ApiAnalyticsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const SmsGatewayPage = lazy(() => import('./pages/SmsGatewayPage'));
const GlobalDashboard = lazy(() => import('./pages/GlobalDashboard'));

// B2B Enterprise Module
const CompaniesPage = lazy(() => import('./pages/CompaniesPage'));
const CompanyProfile360 = lazy(() => import('./components/CompanyProfile360'));

// Strony klientów (rozliczenie dotacji)
const ClientWebsitesPage = lazy(() => import('./pages/ClientWebsitesPage'));
const RetentionBoardPage = lazy(() => import('./pages/RetentionBoardPage'));
const SupportDashboardPage = lazy(() => import('./pages/SupportDashboardPage'));
const MonitoringDashboard = lazy(() => import('./pages/MonitoringDashboard'));
const GrantMonitorPage = lazy(() => import('./pages/GrantMonitorPage'));
const GrantPortfolioPage = lazy(() => import('./pages/GrantPortfolioPage'));
const AngelsPage = lazy(() => import('./pages/AngelsPage'));
const CrossSellPage = lazy(() => import('./pages/CrossSellPage'));
const MigrationPanel = lazy(() => import('./pages/MigrationPanel'));
const InfrastructureOverviewPage = lazy(() => import('./pages/InfrastructureOverviewPage'));
const BackupManagerPage = lazy(() => import('./pages/BackupManagerPage'));

// Foundation module
const FoundationProjectsPage = lazy(() => import('./pages/FoundationProjectsPage'));
const FoundationProjectDetail = lazy(() => import('./pages/FoundationProjectDetail'));
const FoundationDonationsPage = lazy(() => import('./pages/FoundationDonationsPage'));
const FoundationAccountingPage = lazy(() => import('./pages/FoundationAccountingPage'));
const FoundationReportsPage = lazy(() => import('./pages/FoundationReportsPage'));
const TripFinancialPage = lazy(() => import('./pages/TripFinancialPage'));
const WycieczkiTermsPage = lazy(() => import('./pages/WycieczkiTermsPage'));
const WycieczkiWplatyPage = lazy(() => import('./pages/WycieczkiWplatyPage'));
const OpenBankingCallbackPage = lazy(() => import('./pages/OpenBankingCallbackPage'));
const FoundationWebsiteManager = lazy(() => import('./pages/FoundationWebsiteManager'));
const TeamMembersPage = lazy(() => import('./pages/TeamMembersPage'));
const PESSupportPage = lazy(() => import('./pages/PESSupportPage'));
const EDoreczeniaPage = lazy(() => import('./pages/EDoreczeniaPage'));
const CloudflareDNS = lazy(() => import('./pages/CloudflareDNS'));
const EmailInboxPage = lazy(() => import('./pages/EmailInboxPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));

// Turnusy module
const TurnusyDashboard = lazy(() => import('./components/turnusy/TurnusyDashboard').then(m => ({ default: m.TurnusyDashboard })));
const TurnusyClientList = lazy(() => import('./components/turnusy/TurnusyClientList').then(m => ({ default: m.TurnusyClientList })));
const TurnusyCenterList = lazy(() => import('./components/turnusy/TurnusyCenterList').then(m => ({ default: m.TurnusyCenterList })));
const TurnusyCenterDetail = lazy(() => import('./components/turnusy/TurnusyCenterDetail').then(m => ({ default: m.TurnusyCenterDetail })));
const TurnusyTripList = lazy(() => import('./components/turnusy/TurnusyTripList').then(m => ({ default: m.TurnusyTripList })));
const TurnusyBookingList = lazy(() => import('./components/turnusy/TurnusyBookingList').then(m => ({ default: m.TurnusyBookingList })));
const TurnusyMatchingPanel = lazy(() => import('./components/turnusy/TurnusyMatchingPanel').then(m => ({ default: m.TurnusyMatchingPanel })));
const ClientProfile360 = lazy(() => import('./pages/Turnusy/ClientProfile360').then(m => ({ default: m.ClientProfile360 })));
const RoomAllocationMatrix = lazy(() => import('./pages/Turnusy/RoomAllocationMatrix').then(m => ({ default: m.RoomAllocationMatrix })));
const TurnusBookingsTable = lazy(() => import('./pages/Turnusy/TurnusBookingsTable').then(m => ({ default: m.TurnusBookingsTable })));
const TurnusyReportsPage = lazy(() => import('./pages/Turnusy/TurnusyReportsPage').then(m => ({ default: m.TurnusyReportsPage })));
const EmpatiaImportPanel = lazy(() => import('./components/turnusy/EmpatiaImportPanel').then(m => ({ default: m.EmpatiaImportPanel })));
const TurnusyBookingDetail = lazy(() => import('./pages/Turnusy/TurnusyBookingDetail').then(m => ({ default: m.TurnusyBookingDetail })));
const TurnusyOrganizerList = lazy(() => import('./components/turnusy/TurnusyOrganizerList').then(m => ({ default: m.TurnusyOrganizerList })));
const TurnusyCampaignList = lazy(() => import('./components/turnusy/TurnusyCampaignList').then(m => ({ default: m.TurnusyCampaignList })));
const TurnusyBatchEnrichmentPage = lazy(() => import('./pages/Turnusy/TurnusyBatchEnrichmentPage').then(m => ({ default: m.TurnusyBatchEnrichmentPage })));
const TurnusyCmsPanel = lazy(() => import('./pages/Turnusy/TurnusyCmsPanel').then(m => ({ default: m.TurnusyCmsPanel })));
const CooperationPipeline = lazy(() => import('./components/turnusy/CooperationPipeline').then(m => ({ default: m.CooperationPipeline })));
const TurnusyRevenueDashboard = lazy(() => import('./components/turnusy/TurnusyRevenueDashboard').then(m => ({ default: m.TurnusyRevenueDashboard })));

// Unified Calendar
const UnifiedCalendarPage = lazy(() => import('./pages/UnifiedCalendarPage'));

// Trainings module (EduGrantOS)
const TrainingsDashboard = lazy(() => import('./pages/Trainings/TrainingsDashboard').then(m => ({ default: m.TrainingsDashboard })));
const TrainingCalendar = lazy(() => import('./pages/Trainings/TrainingCalendar').then(m => ({ default: m.TrainingCalendar })));
const SessionDetails = lazy(() => import('./pages/Trainings/SessionDetails').then(m => ({ default: m.SessionDetails })));
const SessionsList = lazy(() => import('./pages/Trainings/SessionsList').then(m => ({ default: m.SessionsList })));
const CoursesManagement = lazy(() => import('./pages/Trainings/CoursesManagement').then(m => ({ default: m.CoursesManagement })));
const TrainersManagement = lazy(() => import('./pages/Trainings/TrainersManagement').then(m => ({ default: m.TrainersManagement })));
const ParticipantsManagement = lazy(() => import('./pages/Trainings/ParticipantsManagement').then(m => ({ default: m.ParticipantsManagement })));
const CompaniesManagement = lazy(() => import('./pages/Trainings/CompaniesManagement').then(m => ({ default: m.CompaniesManagement })));
const BurIntegrationPanel = lazy(() => import('./pages/Trainings/BurIntegrationPanel').then(m => ({ default: m.BurIntegrationPanel })));
const CourseDetail = lazy(() => import('./pages/Trainings/CourseDetail').then(m => ({ default: m.CourseDetail })));
const TrainerDetail = lazy(() => import('./pages/Trainings/TrainerDetail').then(m => ({ default: m.TrainerDetail })));
const TrainingReportsPage = lazy(() => import('./pages/Trainings/TrainingReportsPage').then(m => ({ default: m.TrainingReportsPage })));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const OperatorBriefingPage = lazy(() => import('./pages/OperatorBriefingPage'));
const ExecutiveDashboardPage = lazy(() => import('./pages/ExecutiveDashboardPage'));
const FoundationCockpitPage = lazy(() => import('./pages/FoundationCockpitPage'));
const FundraisingDashboardPage = lazy(() => import('./pages/FundraisingDashboardPage'));
const ResourcesManagement = lazy(() => import('./pages/Trainings/ResourcesManagement'));
const TestsManagement = lazy(() => import('./pages/Trainings/TestsManagement'));
const GFCoursesManagement = lazy(() => import('./pages/Trainings/GFCoursesManagement').then(m => ({ default: m.GFCoursesManagement })));
const KFSManagement = lazy(() => import('./pages/Trainings/KFSManagement').then(m => ({ default: m.KFSManagement })));
const KfsHandlowiecDashboard = lazy(() => import('./pages/Trainings/KfsHandlowiecDashboard').then(m => ({ default: m.KfsHandlowiecDashboard })));
const KfsPowerPanel = lazy(() => import('./pages/Trainings/KfsPowerPanel'));
const KfsLeadImport = lazy(() => import('./pages/Trainings/KfsLeadImport'));
const KfsExcelImportPage = lazy(() => import('./pages/KfsExcelImportPage'));
const KfsKanbanBoard = lazy(() => import('./pages/Trainings/KfsKanbanBoard').then(m => ({ default: m.KfsKanbanBoard })));
const KfsEmailComposer = lazy(() => import('./pages/Trainings/KfsEmailComposer'));
const KfsNotifications = lazy(() => import('./pages/Trainings/KfsNotifications'));
const KfsAutomationRules = lazy(() => import('./pages/Trainings/KfsAutomationRules'));
const KfsDashboardHub = lazy(() => import('./pages/Trainings/KfsDashboardHub'));
const KfsPupTimeline = lazy(() => import('./pages/Trainings/KfsPupTimeline'));
const KfsActivityStream = lazy(() => import('./pages/Trainings/KfsActivityStream').then(m => ({ default: m.KfsActivityStream })));
const KfsPerformanceTracker = lazy(() => import('./pages/Trainings/KfsPerformanceTracker'));
const KfsScoringTuner = lazy(() => import('./pages/Trainings/KfsScoringTuner'));
const KfsComplianceOverview = lazy(() => import('./pages/Trainings/KfsComplianceOverview'));
const KfsEnrollmentBridge = lazy(() => import('./pages/Trainings/KfsEnrollmentBridge'));
const KfsCompany360 = lazy(() => import('./pages/Trainings/KfsCompany360'));
const KfsSmartScheduler = lazy(() => import('./pages/Trainings/KfsSmartScheduler'));
const KfsFollowUpSequences = lazy(() => import('./pages/Trainings/KfsFollowUpSequences'));
const KfsTerritoryMap = lazy(() => import('./pages/Trainings/KfsTerritoryMap'));
const KfsBulkActions = lazy(() => import('./pages/Trainings/KfsBulkActions'));
const KfsProposalGenerator = lazy(() => import('./pages/Trainings/KfsProposalGenerator'));
const KfsMeetingNotes = lazy(() => import('./pages/Trainings/KfsMeetingNotes'));
const KfsPipelineAnalytics = lazy(() => import('./pages/Trainings/KfsPipelineAnalytics'));
const KfsDocumentTemplates = lazy(() => import('./pages/Trainings/KfsDocumentTemplates'));
const KfsIntegrationHub = lazy(() => import('./pages/Trainings/KfsIntegrationHub'));
const KfsGoalTracker = lazy(() => import('./pages/Trainings/KfsGoalTracker'));
const KfsCompetitorAnalysis = lazy(() => import('./pages/Trainings/KfsCompetitorAnalysis'));
const KfsClientPortal = lazy(() => import('./pages/Trainings/KfsClientPortal'));
const KfsTrainingCatalog = lazy(() => import('./pages/Trainings/KfsTrainingCatalog'));
const KfsROICalculator = lazy(() => import('./pages/Trainings/KfsROICalculator'));
const KfsSmsComposer = lazy(() => import('./pages/Trainings/KfsSmsComposer'));
const KfsTaskManager = lazy(() => import('./pages/Trainings/KfsTaskManager'));
const KfsQualificationWizard = lazy(() => import('./pages/Trainings/KfsQualificationWizard'));
const KfsReferralProgram = lazy(() => import('./pages/Trainings/KfsReferralProgram'));
const KfsReportingDashboard = lazy(() => import('./pages/Trainings/KfsReportingDashboard'));
const KfsContractManager = lazy(() => import('./pages/Trainings/KfsContractManager'));
const KfsFinancialOverview = lazy(() => import('./pages/Trainings/KfsFinancialOverview'));
const KfsTrainingCalendarView = lazy(() => import('./pages/Trainings/KfsTrainingCalendarView'));
const KfsTeamWorkload = lazy(() => import('./pages/Trainings/KfsTeamWorkload'));
const KfsFunnelOptimizer = lazy(() => import('./pages/Trainings/KfsFunnelOptimizer'));
const KfsDocumentVault = lazy(() => import('./pages/Trainings/KfsDocumentVault'));
const KfsLeadTimeline = lazy(() => import('./pages/Trainings/KfsLeadTimeline'));
const KfsNaborComparison = lazy(() => import('./pages/Trainings/KfsNaborComparison'));
const KfsEmailTracker = lazy(() => import('./pages/Trainings/KfsEmailTracker'));
const KfsRegionHeatmap = lazy(() => import('./pages/Trainings/KfsRegionHeatmap'));
const KfsWinLossAnalysis = lazy(() => import('./pages/Trainings/KfsWinLossAnalysis'));
const KfsContactLog = lazy(() => import('./pages/Trainings/KfsContactLog'));
const KfsBudgetPlanner = lazy(() => import('./pages/Trainings/KfsBudgetPlanner'));
const KfsLeadScorecard = lazy(() => import('./pages/Trainings/KfsLeadScorecard'));
const KfsCampaignManager = lazy(() => import('./pages/Trainings/KfsCampaignManager'));
const KfsInvoiceTracker = lazy(() => import('./pages/Trainings/KfsInvoiceTracker'));
const KfsTrainerDirectory = lazy(() => import('./pages/Trainings/KfsTrainerDirectory'));
const KfsPupDirectory = lazy(() => import('./pages/Trainings/KfsPupDirectory'));
const KfsLeadDeduplicator = lazy(() => import('./pages/Trainings/KfsLeadDeduplicator'));
const KfsSuccessStories = lazy(() => import('./pages/Trainings/KfsSuccessStories'));
const KfsRenewalTracker = lazy(() => import('./pages/Trainings/KfsRenewalTracker'));
const KfsCompetitorIntel = lazy(() => import('./pages/Trainings/KfsCompetitorIntel'));
const KfsApplicationWizard = lazy(() => import('./pages/Trainings/KfsApplicationWizard'));
const KfsPartnerNetwork = lazy(() => import('./pages/Trainings/KfsPartnerNetwork'));
const KfsMarketAnalysis = lazy(() => import('./pages/Trainings/KfsMarketAnalysis'));
const KfsDocumentGenerator = lazy(() => import('./pages/Trainings/KfsDocumentGenerator'));
const KfsMeetingScheduler = lazy(() => import('./pages/Trainings/KfsMeetingScheduler'));
const KfsABTestTracker = lazy(() => import('./pages/Trainings/KfsABTestTracker'));
const KfsChurnPredictor = lazy(() => import('./pages/Trainings/KfsChurnPredictor'));
const KfsSatisfactionSurvey = lazy(() => import('./pages/Trainings/KfsSatisfactionSurvey'));
const KfsQuotationBuilder = lazy(() => import('./pages/Trainings/KfsQuotationBuilder'));
const KfsWebhookManager = lazy(() => import('./pages/Trainings/KfsWebhookManager'));
const KfsNaborCalendar = lazy(() => import('./pages/Trainings/KfsNaborCalendar'));
const KfsCompanyProfile = lazy(() => import('./pages/Trainings/KfsCompanyProfile'));
const KfsSalesScript = lazy(() => import('./pages/Trainings/KfsSalesScript'));
// R21
const KfsEventCalendar = lazy(() => import('./pages/Trainings/KfsEventCalendar'));
const KfsAuditLog = lazy(() => import('./pages/Trainings/KfsAuditLog'));
const KfsPricingStrategy = lazy(() => import('./pages/Trainings/KfsPricingStrategy'));
const KfsClientFeedback = lazy(() => import('./pages/Trainings/KfsClientFeedback'));
const KfsCertificateManager = lazy(() => import('./pages/Trainings/KfsCertificateManager'));
// R22
const KfsTrainerManagement = lazy(() => import('./pages/Trainings/KfsTrainerManagement'));
const KfsRevenueForecaster = lazy(() => import('./pages/Trainings/KfsRevenueForecaster'));
const KfsEmailSequence = lazy(() => import('./pages/Trainings/KfsEmailSequence'));
const KfsDataExport = lazy(() => import('./pages/Trainings/KfsDataExport'));
const KfsPupRelationship = lazy(() => import('./pages/Trainings/KfsPupRelationship'));
// R23
const KfsOnboarding = lazy(() => import('./pages/Trainings/KfsOnboarding'));
const KfsDocumentChecklist = lazy(() => import('./pages/Trainings/KfsDocumentChecklist'));
const KfsComplianceCalendar = lazy(() => import('./pages/Trainings/KfsComplianceCalendar'));
const KfsTeamPerformanceDash = lazy(() => import('./pages/Trainings/KfsTeamPerformanceDash'));
const KfsMarketingCampaign = lazy(() => import('./pages/Trainings/KfsMarketingCampaign'));
// KFS Lead Dashboard + Training Programs (pages/)
const KfsLeadDashboard = lazy(() => import('./pages/KfsLeadDashboard'));
const KfsTrainingProgramsPage = lazy(() => import('./pages/KfsTrainingProgramsPage'));

// R24
const KfsVoipLogger = lazy(() => import('./pages/Trainings/KfsVoipLogger'));
const KfsContentLibrary = lazy(() => import('./pages/Trainings/KfsContentLibrary'));
const KfsDeadlineTracker = lazy(() => import('./pages/Trainings/KfsDeadlineTracker'));
const KfsCustomReports = lazy(() => import('./pages/Trainings/KfsCustomReports'));
const KfsWorkflowBuilder = lazy(() => import('./pages/Trainings/KfsWorkflowBuilder'));
const KfsExecutiveDashboard = lazy(() => import('./pages/Trainings/KfsExecutiveDashboard'));

// Client Lead Scoring
const ClientScoringPage = lazy(() => import('./pages/ClientScoringPage'));

// Client Health Score
const ClientHealthPage = lazy(() => import('./pages/ClientHealthPage'));

// Visitor Management
const VisitorManagementPage = lazy(() => import('./pages/VisitorManagementPage'));

// Learning Progress
const LearningProgressPage = lazy(() => import('./pages/LearningProgressPage'));

// OCR Documents
const OcrDocumentsPage = lazy(() => import('./pages/OcrDocumentsPage'));
const OcrScannerPage = lazy(() => import('./pages/OcrScannerPage'));
const DuplicatesPage = lazy(() => import('./pages/DuplicatesPage'));
const IndustryManagerPage = lazy(() => import('./pages/IndustryManagerPage'));
const TagsManagerPage = lazy(() => import('./pages/TagsManagerPage'));
const CronJobsPage = lazy(() => import('./pages/CronJobsPage'));

// Google Workspace Admin
const GoogleWorkspaceManager = lazy(() => import('./pages/GoogleWorkspaceManager'));
const GoogleWorkspaceAdminPage = lazy(() => import('./pages/GoogleWorkspaceAdminPage'));
const GoogleIntegrationsPage = lazy(() => import('./pages/GoogleIntegrationsPage'));
const GoogleDrivePage = lazy(() => import('./pages/GoogleDrivePage'));
const AnalyticsDashboardPage = lazy(() => import('./pages/AnalyticsDashboardPage'));
const BusinessIntelligencePage = lazy(() => import('./pages/BusinessIntelligencePage'));

// Grant Calls (nabory dotacyjne) + Matching + Settlements + Operators
const GrantFpnForm = lazy(() => import('./components/GrantFpnForm'));
const GrantFpnPublicForm = lazy(() => import('./components/GrantFpnPublicForm'));
const GrantKnowledgePage = lazy(() => import('./components/GrantKnowledgePage'));
const GrantCallsPage = lazy(() => import('./pages/GrantCallsPage'));
const GrantMatchingPage = lazy(() => import('./pages/GrantMatchingPage'));
const GrantSettlementsPage = lazy(() => import('./pages/GrantSettlementsPage'));
const GrantOperatorsPage = lazy(() => import('./pages/GrantOperatorsPage'));
const ScrapeMonitorPage = lazy(() => import('./pages/ScrapeMonitorPage'));
const GrantMapPage = lazy(() => import('./pages/GrantMapPage'));
const PublicGrantMapPage = lazy(() => import('./pages/PublicGrantMapPage'));
const PublicGrantSearchPage = lazy(() => import('./pages/PublicGrantSearchPage'));

// Contest (public + admin)
const ContestPage = lazy(() => import('./pages/ContestPage'));
const ContestPageV2 = lazy(() => import('./pages/ContestPageV2'));
const ContestAdminPage = lazy(() => import('./pages/ContestAdminPage'));
const ContestLeaderboardPage = lazy(() => import('./pages/ContestLeaderboardPage'));
const CebroAdminPage = lazy(() => import('./pages/CebroAdminPage'));
const AdminCenterPage = lazy(() => import('./pages/AdminCenterPage'));

// Partners (krakow.business)
const PartnersPage = lazy(() => import('./pages/PartnersPage'));

// B2B Portal
const B2BIssuesPage = lazy(() => import('./pages/B2BPortal/IssuesPage'));
const B2BIssueDetail = lazy(() => import('./pages/B2BPortal/IssueDetail'));
const B2BNewIssuePage = lazy(() => import('./pages/B2BPortal/NewIssuePage'));

// Sklep Nowohucki
const SklepPage = lazy(() => import('./pages/SklepPage'));

// SINCE 1950 — Sklep Kibica Hutnika Kraków
const SincePage = lazy(() => import('./pages/SincePage'));

// KFS Panel
const KfsPanelPage = lazy(() => import('./pages/KfsPanelPage'));

// Documents Manager
const DocumentsManagerPage = lazy(() => import('./pages/DocumentsManagerPage'));

// Workflow & Reports
const WorkflowBuilderPage = lazy(() => import('./pages/WorkflowBuilderPage'));
const DocumentWorkflowPage = lazy(() => import('./pages/DocumentWorkflowPage'));
const WeeklyReportsPage = lazy(() => import('./pages/WeeklyReportsPage'));
const SmartDocumentsPage = lazy(() => import('./pages/SmartDocumentsPage'));
const BulkOperationsPage = lazy(() => import('./pages/BulkOperationsPage'));
const IntegrationHubPage = lazy(() => import('./pages/IntegrationHubPage'));
const KSeFPage = lazy(() => import('./pages/KSeFPage'));
const RodoCompliancePage = lazy(() => import('./pages/RodoCompliancePage'));
const NotificationCenterPage = lazy(() => import('./pages/NotificationCenterPage'));
const AIScoringPage = lazy(() => import('./pages/AIScoringPage'));
const AppointmentsPage = lazy(() => import('./pages/AppointmentsPage'));
const OperatorPerformancePage = lazy(() => import('./pages/OperatorPerformancePage'));
const DocumentTemplatesPage = lazy(() => import('./pages/DocumentTemplatesPage'));
const ClientTimelinePage = lazy(() => import('./pages/ClientTimelinePage'));
const PipelineForecastPage = lazy(() => import('./pages/PipelineForecastPage'));
const SLAMonitorPage = lazy(() => import('./pages/SLAMonitorPage'));
const SLADashboardPage = lazy(() => import('./pages/SLADashboardPage'));
const GrantCalculatorPage = lazy(() => import('./pages/GrantCalculatorPage'));
const DailyDigestPage = lazy(() => import('./pages/DailyDigestPage'));
const ContractorsPage = lazy(() => import('./pages/ContractorsPage'));
const HeatmapPage = lazy(() => import('./pages/HeatmapPage'));
const ExportCenterPage = lazy(() => import('./pages/ExportCenterPage'));
const ProjectBudgetPage = lazy(() => import('./pages/ProjectBudgetPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const FeedbackSurveyPage = lazy(() => import('./pages/FeedbackSurveyPage'));
const FeedbackWallPage = lazy(() => import('./pages/FeedbackWallPage'));
const NPSTrackerPage = lazy(() => import('./pages/NPSTrackerPage'));
const OKRGoalsPage = lazy(() => import('./pages/OKRGoalsPage'));
const ContractRegistryPage = lazy(() => import('./pages/ContractRegistryPage'));
const FinanceDashboardPage = lazy(() => import('./pages/FinanceDashboardPage'));
const RevenueForecastPage = lazy(() => import('./pages/RevenueForecastPage'));
const InternalWikiPage = lazy(() => import('./pages/InternalWikiPage'));
const AssetManagementPage = lazy(() => import('./pages/AssetManagementPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const ROICalculatorPage = lazy(() => import('./pages/ROICalculatorPage'));
const ComplianceChecklistPage = lazy(() => import('./pages/ComplianceChecklistPage'));
const QualityAssurancePage = lazy(() => import('./pages/QualityAssurancePage'));
const ClientSegmentsPage = lazy(() => import('./pages/ClientSegmentsPage'));
const ActivityLogPage = lazy(() => import('./pages/ActivityLogPage'));
const AuditTrailPage = lazy(() => import('./pages/AuditTrailPage'));
const TaskBoardPage = lazy(() => import('./pages/TaskBoardPage'));
const EmailAutomationPage = lazy(() => import('./pages/EmailAutomationPage'));
const LeadNurturingPage = lazy(() => import('./pages/LeadNurturingPage'));
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplatesPage'));
const CostTrackerPage = lazy(() => import('./pages/CostTrackerPage'));
const TimeTrackingPage = lazy(() => import('./pages/TimeTrackingPage'));
const HelpdeskPage = lazy(() => import('./pages/HelpdeskPage'));
const CommissionsPage = lazy(() => import('./pages/CommissionsPage'));
const ServiceCatalogPage = lazy(() => import('./pages/ServiceCatalogPage'));
const RiskRegisterPage = lazy(() => import('./pages/RiskRegisterPage'));
const RiskAssessmentPage = lazy(() => import('./pages/RiskAssessmentPage'));
const Client360Page = lazy(() => import('./pages/Client360Page'));
const InternalNotificationsPage = lazy(() => import('./pages/InternalNotificationsPage'));
const ReportGeneratorPage = lazy(() => import('./pages/ReportGeneratorPage'));
const ESignaturePage = lazy(() => import('./pages/ESignaturePage'));
const SaasInventoryPage = lazy(() => import('./pages/SaasInventoryPage'));
const VendorManagementPage = lazy(() => import('./pages/VendorManagementPage'));
const ProcurementPage = lazy(() => import('./pages/ProcurementPage'));
const ExpenseClaimsPage = lazy(() => import('./pages/ExpenseClaimsPage'));
const TravelExpensesPage = lazy(() => import('./pages/TravelExpensesPage'));
const TeamKnowledgeBasePage = lazy(() => import('./pages/TeamKnowledgeBasePage'));
const MeetingNotesPage = lazy(() => import('./pages/MeetingNotesPage'));
const OfficeInventoryPage = lazy(() => import('./pages/OfficeInventoryPage'));
const DataQualityPage = lazy(() => import('./pages/DataQualityPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const CustomFormsPage = lazy(() => import('./pages/CustomFormsPage'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const GoalTrackerPage = lazy(() => import('./pages/GoalTrackerPage'));
const ShiftSchedulerPage = lazy(() => import('./pages/ShiftSchedulerPage'));
const CrmAiAnalysisPage = lazy(() => import('./pages/CrmAiAnalysisPage'));
const CenterEnrichmentPage = lazy(() => import('./pages/CenterEnrichmentPage'));
const PaymentTrackingPage = lazy(() => import('./pages/PaymentTrackingPage'));
const WebhookManagerPage = lazy(() => import('./pages/WebhookManagerPage'));
const CustomerJourneyPage = lazy(() => import('./pages/CustomerJourneyPage'));
const SprintBoardPage = lazy(() => import('./pages/SprintBoardPage'));
const PolicyManagerPage = lazy(() => import('./pages/PolicyManagerPage'));
const IncidentManagementPage = lazy(() => import('./pages/IncidentManagementPage'));

const KnowledgeGraphPage = lazy(() => import('./pages/KnowledgeGraphPage'));
const TenantManagerPage = lazy(() => import('./pages/TenantManagerPage'));
const ClientOnboardingPage = lazy(() => import('./pages/ClientOnboardingPage'));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'));
const BrandAssetsPage = lazy(() => import('./pages/BrandAssetsPage'));
const SurveyBuilderPage = lazy(() => import('./pages/SurveyBuilderPage'));
const CompetitorTrackerPage = lazy(() => import('./pages/CompetitorTrackerPage'));
const ProjectTimelinePage = lazy(() => import('./pages/ProjectTimelinePage'));
const ResourcePlannerPage = lazy(() => import('./pages/ResourcePlannerPage'));
const CapacityForecastPage = lazy(() => import('./pages/CapacityForecastPage'));
const InventoryManagementPage = lazy(() => import('./pages/InventoryManagementPage'));
const EventManagerPage = lazy(() => import('./pages/EventManagerPage'));
const ReferralProgramPage = lazy(() => import('./pages/ReferralProgramPage'));
const KnowledgeBaseV2Page = lazy(() => import('./pages/KnowledgeBaseV2Page'));
const SubscriptionManagerPage = lazy(() => import('./pages/SubscriptionManagerPage'));
const MeetingRoomsPage = lazy(() => import('./pages/MeetingRoomsPage'));
const ChangeRequestsPage = lazy(() => import('./pages/ChangeRequestsPage'));
const ProposalGeneratorPage = lazy(() => import('./pages/ProposalGeneratorPage'));
const CSATTrackerPage = lazy(() => import('./pages/CSATTrackerPage'));
const DocumentScannerPage = lazy(() => import('./pages/DocumentScannerPage'));
const ContractLifecyclePage = lazy(() => import('./pages/ContractLifecyclePage'));
const NotificationRulesPage = lazy(() => import('./pages/NotificationRulesPage'));
const AbsenceManagerPage = lazy(() => import('./pages/AbsenceManagerPage'));
const FleetManagementPage = lazy(() => import('./pages/FleetManagementPage'));
// R16 modules
const ProductCatalogPage = lazy(() => import('./pages/ProductCatalogPage'));
const TestimonialManagerPage = lazy(() => import('./pages/TestimonialManagerPage'));
const CampaignROIPage = lazy(() => import('./pages/CampaignROIPage'));
const EmployeeDirectoryPage = lazy(() => import('./pages/EmployeeDirectoryPage'));
const IdeaBoardPage = lazy(() => import('./pages/IdeaBoardPage'));
const CertificationTrackerPage = lazy(() => import('./pages/CertificationTrackerPage'));
const KnowledgeQuizPage = lazy(() => import('./pages/KnowledgeQuizPage'));
const BudgetPlannerPage = lazy(() => import('./pages/BudgetPlannerPage'));
const VendorScorecardPage = lazy(() => import('./pages/VendorScorecardPage'));
const WhiteboardPage = lazy(() => import('./pages/WhiteboardPage'));
// R17 modules
const VisitorLogPage = lazy(() => import('./pages/VisitorLogPage'));
const PayrollOverviewPage = lazy(() => import('./pages/PayrollOverviewPage'));
const DocumentArchivePage = lazy(() => import('./pages/DocumentArchivePage'));
const EquipmentMaintenancePage = lazy(() => import('./pages/EquipmentMaintenancePage'));
const ReleaseNotesPage = lazy(() => import('./pages/ReleaseNotesPage'));
const ServiceDeskPage = lazy(() => import('./pages/ServiceDeskPage'));
const WorkspaceBookingPage = lazy(() => import('./pages/WorkspaceBookingPage'));
const KnowledgeFaqPage = lazy(() => import('./pages/KnowledgeFaqPage'));
const ComplianceTrainingPage = lazy(() => import('./pages/ComplianceTrainingPage'));
// R18 modules
const PortalConfigPage = lazy(() => import('./pages/PortalConfigPage'));
const TemplateLibraryPage = lazy(() => import('./pages/TemplateLibraryPage'));
const CostCenterPage = lazy(() => import('./pages/CostCenterPage'));
const SOPManagerPage = lazy(() => import('./pages/SOPManagerPage'));
const CommunicationLogPage = lazy(() => import('./pages/CommunicationLogPage'));
const DataImportPage = lazy(() => import('./pages/DataImportPage'));
const DashboardBuilderPage = lazy(() => import('./pages/DashboardBuilderPage'));
const AccessControlPage = lazy(() => import('./pages/AccessControlPage'));
const PriceListPage = lazy(() => import('./pages/PriceListPage'));
const SystemHealthPage = lazy(() => import('./pages/SystemHealthPage'));
// R19 modules
const OnboardingChecklistPage = lazy(() => import('./pages/OnboardingChecklistPage'));
const DeliveryTrackerPage = lazy(() => import('./pages/DeliveryTrackerPage'));
const ClientContractsPage = lazy(() => import('./pages/ClientContractsPage'));
const PartnerPortalPage = lazy(() => import('./pages/PartnerPortalPage'));
const MeetingAgendaPage = lazy(() => import('./pages/MeetingAgendaPage'));
const AssetLifecyclePage = lazy(() => import('./pages/AssetLifecyclePage'));
const ApprovalWorkflowPage = lazy(() => import('./pages/ApprovalWorkflowPage'));
const MailingListManagerPage = lazy(() => import('./pages/MailingListManagerPage'));
const LegalCaseTrackerPage = lazy(() => import('./pages/LegalCaseTrackerPage'));
const EnergyMonitorPage = lazy(() => import('./pages/EnergyMonitorPage'));
// R20 modules
const ProjectMilestonePage = lazy(() => import('./pages/ProjectMilestonePage'));
const DailyStandupPage = lazy(() => import('./pages/DailyStandupPage'));
const ParkingManagerPage = lazy(() => import('./pages/ParkingManagerPage'));
const BusinessCardScannerPage = lazy(() => import('./pages/BusinessCardScannerPage'));
const OfficeSuppliesPage = lazy(() => import('./pages/OfficeSuppliesPage'));
const ClientFeedbackBoardPage = lazy(() => import('./pages/ClientFeedbackBoardPage'));
const TrainingNeedsPage = lazy(() => import('./pages/TrainingNeedsPage'));
const EmergencyContactsPage = lazy(() => import('./pages/EmergencyContactsPage'));
const PrintLabelsPage = lazy(() => import('./pages/PrintLabelsPage'));
const WeatherWidgetPage = lazy(() => import('./pages/WeatherWidgetPage'));
// R21 modules
const KpiDashboardPage = lazy(() => import('./pages/KpiDashboardPage'));
const ContentCalendarPage = lazy(() => import('./pages/ContentCalendarPage'));
const DonationTrackerPage = lazy(() => import('./pages/DonationTrackerPage'));
const SatisfactionIndexPage = lazy(() => import('./pages/SatisfactionIndexPage'));
const OfficeBookshelfPage = lazy(() => import('./pages/OfficeBookshelfPage'));
const TeamMoodPage = lazy(() => import('./pages/TeamMoodPage'));
const BenefitManagerPage = lazy(() => import('./pages/BenefitManagerPage'));
const ServerInventoryPage = lazy(() => import('./pages/ServerInventoryPage'));
const RetrospectivePage = lazy(() => import('./pages/RetrospectivePage'));
const PasswordVaultPage = lazy(() => import('./pages/PasswordVaultPage'));
// R23 modules
const LoyaltyProgramPage = lazy(() => import('./pages/LoyaltyProgramPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const FormAnalyticsPage = lazy(() => import('./pages/FormAnalyticsPage'));
const DataExportPage = lazy(() => import('./pages/DataExportPage'));
// R26 modules
const CompetitorAnalysisPage = lazy(() => import('./pages/CompetitorAnalysisPage'));
const ChurnPredictorPage = lazy(() => import('./pages/ChurnPredictorPage'));
const CampaignManagerPage = lazy(() => import('./pages/CampaignManagerPage'));
const QuoteBuilderPage = lazy(() => import('./pages/QuoteBuilderPage'));
const DealPipelinePage = lazy(() => import('./pages/DealPipelinePage'));
const MeetingSchedulerPage = lazy(() => import('./pages/MeetingSchedulerPage'));
const InvoiceGeneratorPage = lazy(() => import('./pages/InvoiceGeneratorPage'));
const FeedbackCollectionPage = lazy(() => import('./pages/FeedbackCollectionPage'));
const NotificationHubPage = lazy(() => import('./pages/NotificationHubPage'));
// R27 modules
const WorkflowEnginePage = lazy(() => import('./pages/WorkflowEnginePage'));
const ReportBuilderPage = lazy(() => import('./pages/ReportBuilderPage'));
const ContactManagerPage = lazy(() => import('./pages/ContactManagerPage'));
const ProductPricingPage = lazy(() => import('./pages/ProductPricingPage'));
const EventSchedulerPage = lazy(() => import('./pages/EventSchedulerPage'));
const TicketSystemPage = lazy(() => import('./pages/TicketSystemPage'));
const ResourceCalendarPage = lazy(() => import('./pages/ResourceCalendarPage'));
const ExpenseManagerPage = lazy(() => import('./pages/ExpenseManagerPage'));
const DocumentGeneratorPage = lazy(() => import('./pages/DocumentGeneratorPage'));
const ActivityFeedPage = lazy(() => import('./pages/ActivityFeedPage'));
// R28 modules
const SalesForecastPage = lazy(() => import('./pages/SalesForecastPage'));
const ClientOnboardingFlowPage = lazy(() => import('./pages/ClientOnboardingFlowPage'));
const PerformanceReviewPage = lazy(() => import('./pages/PerformanceReviewPage'));
const TaskAutomationPage = lazy(() => import('./pages/TaskAutomationPage'));
const RiskDashboardPage = lazy(() => import('./pages/RiskDashboardPage'));
const ClientPortalBuilderPage = lazy(() => import('./pages/ClientPortalBuilderPage'));
const SmsCampaignPage = lazy(() => import('./pages/SmsCampaignPage'));
const ProjectPortfolioPage = lazy(() => import('./pages/ProjectPortfolioPage'));
const DigitalSignaturePage = lazy(() => import('./pages/DigitalSignaturePage'));
// R29 modules
const CalendarSyncPage = lazy(() => import('./pages/CalendarSyncPage'));
const FileManagerPage = lazy(() => import('./pages/FileManagerPage'));
const TeamDirectoryPage = lazy(() => import('./pages/TeamDirectoryPage'));
const ClientNotesPage = lazy(() => import('./pages/ClientNotesPage'));
const TrainingCalendarPage = lazy(() => import('./pages/TrainingCalendarPage'));
const VendorPortalPage = lazy(() => import('./pages/VendorPortalPage'));
const SlaManagerPage = lazy(() => import('./pages/SlaManagerPage'));
const ContractTemplatePage = lazy(() => import('./pages/ContractTemplatePage'));
const DashboardAnalyticsPage = lazy(() => import('./pages/DashboardAnalyticsPage'));
const ComplianceCalendarPage = lazy(() => import('./pages/ComplianceCalendarPage'));
// R30 modules
const EmailCampaignBuilderPage = lazy(() => import('./pages/EmailCampaignBuilderPage'));
const FeedbackLoopPage = lazy(() => import('./pages/FeedbackLoopPage'));
const ProcessModelerPage = lazy(() => import('./pages/ProcessModelerPage'));
const DataVisualizationPage = lazy(() => import('./pages/DataVisualizationPage'));
const KnowledgeHubPage = lazy(() => import('./pages/KnowledgeHubPage'));
const UserPreferencesPage = lazy(() => import('./pages/UserPreferencesPage'));
const ProjectDependenciesPage = lazy(() => import('./pages/ProjectDependenciesPage'));
const TimeOffRequestsPage = lazy(() => import('./pages/TimeOffRequestsPage'));
const NotificationTemplatesPage = lazy(() => import('./pages/NotificationTemplatesPage'));
// R31 modules
const ApiRateMonitorPage = lazy(() => import('./pages/ApiRateMonitorPage'));
const ClientActivityScorePage = lazy(() => import('./pages/ClientActivityScorePage'));
const AnnouncementBoardPage = lazy(() => import('./pages/AnnouncementBoardPage'));
const MarketingFunnelPage = lazy(() => import('./pages/MarketingFunnelPage'));
const RevenueAnalyticsPage = lazy(() => import('./pages/RevenueAnalyticsPage'));
const FeatureRequestBoardPage = lazy(() => import('./pages/FeatureRequestBoardPage'));
const TableBrowserPage = lazy(() => import('./pages/TableBrowserPage'));
const HealthCheckDashboardPage = lazy(() => import('./pages/HealthCheckDashboardPage'));
const TagManagerPage = lazy(() => import('./pages/TagManagerPage'));
const SystemConfigPage = lazy(() => import('./pages/SystemConfigPage'));
// R32
const EmployeeOnboardingPage = lazy(() => import('./pages/EmployeeOnboardingPage'));
const ServiceRequestPage = lazy(() => import('./pages/ServiceRequestPage'));
const CostAllocationPage = lazy(() => import('./pages/CostAllocationPage'));
const ClientEngagementPage = lazy(() => import('./pages/ClientEngagementPage'));
const SkillsMatrixPage = lazy(() => import('./pages/SkillsMatrixPage'));
const DocumentApprovalPage = lazy(() => import('./pages/DocumentApprovalPage'));
const ClientSuccessPage = lazy(() => import('./pages/ClientSuccessPage'));
const InventoryAlertsPage = lazy(() => import('./pages/InventoryAlertsPage'));
const MeetingMinutesPage = lazy(() => import('./pages/MeetingMinutesPage'));
const OpsDashboardPage = lazy(() => import('./pages/OpsDashboardPage'));
// R33
const SupplyChainPage = lazy(() => import('./pages/SupplyChainPage'));
const EmployeeRecognitionPage = lazy(() => import('./pages/EmployeeRecognitionPage'));
const CapacityPlanningPage = lazy(() => import('./pages/CapacityPlanningPage'));
const ReleaseManagementPage = lazy(() => import('./pages/ReleaseManagementPage'));
const ApiKeyManagerPage = lazy(() => import('./pages/ApiKeyManagerPage'));
const GanttChartPage = lazy(() => import('./pages/GanttChartPage'));
const MultiCurrencyPage = lazy(() => import('./pages/MultiCurrencyPage'));
const ClientSegmentationPage = lazy(() => import('./pages/ClientSegmentationPage'));
const KanbanBoardPage = lazy(() => import('./pages/KanbanBoardPage'));
const WorkspaceSettingsPage = lazy(() => import('./pages/WorkspaceSettingsPage'));
// R34
const SubscriptionBillingPage = lazy(() => import('./pages/SubscriptionBillingPage'));
const ComplianceAuditPage = lazy(() => import('./pages/ComplianceAuditPage'));
const WorkOrderPage = lazy(() => import('./pages/WorkOrderPage'));
const TerritoryManagerPage = lazy(() => import('./pages/TerritoryManagerPage'));
const IdeaVotingPage = lazy(() => import('./pages/IdeaVotingPage'));
const BatchOperationsPage = lazy(() => import('./pages/BatchOperationsPage'));
const ClientPortfolioPage = lazy(() => import('./pages/ClientPortfolioPage'));
const ChangeLogViewerPage = lazy(() => import('./pages/ChangeLogViewerPage'));
const ScheduledTasksPage = lazy(() => import('./pages/ScheduledTasksPage'));
// R35
const WarehouseManagerPage = lazy(() => import('./pages/WarehouseManagerPage'));
const ContractNegotiationPage = lazy(() => import('./pages/ContractNegotiationPage'));
const CreditScorePage = lazy(() => import('./pages/CreditScorePage'));
const DocumentVersioningPage = lazy(() => import('./pages/DocumentVersioningPage'));
const ForecastingEnginePage = lazy(() => import('./pages/ForecastingEnginePage'));
const PolicyCompliancePage = lazy(() => import('./pages/PolicyCompliancePage'));
const TenantBillingPage = lazy(() => import('./pages/TenantBillingPage'));
const WinLossAnalysisPage = lazy(() => import('./pages/WinLossAnalysisPage'));
const DataDictionaryPage = lazy(() => import('./pages/DataDictionaryPage'));
const OpsPlaybookPage = lazy(() => import('./pages/OpsPlaybookPage'));
// R36
const CommissionCalculatorPage = lazy(() => import('./pages/CommissionCalculatorPage'));
const ProjectRiskMatrixPage = lazy(() => import('./pages/ProjectRiskMatrixPage'));
const StakeholderMapPage = lazy(() => import('./pages/StakeholderMapPage'));
const EmailSignatureManagerPage = lazy(() => import('./pages/EmailSignatureManagerPage'));
const BusinessContinuityPage = lazy(() => import('./pages/BusinessContinuityPage'));
const OkrTrackerPage = lazy(() => import('./pages/OkrTrackerPage'));
const ComplianceEvidencePage = lazy(() => import('./pages/ComplianceEvidencePage'));
const ApiPlaygroundPage = lazy(() => import('./pages/ApiPlaygroundPage'));
const VendorEvaluationPage = lazy(() => import('./pages/VendorEvaluationPage'));
const SystemAnnouncementsPage = lazy(() => import('./pages/SystemAnnouncementsPage'));
// R37
const BenchmarkAnalyticsPage = lazy(() => import('./pages/BenchmarkAnalyticsPage'));
const CustomerRetentionPage = lazy(() => import('./pages/CustomerRetentionPage'));
const IncidentPostmortemPage = lazy(() => import('./pages/IncidentPostmortemPage'));
const ResourceUtilizationPage = lazy(() => import('./pages/ResourceUtilizationPage'));
const ClientRenewalPage = lazy(() => import('./pages/ClientRenewalPage'));
const AutomationLogPage = lazy(() => import('./pages/AutomationLogPage'));
const PaymentGatewayPage = lazy(() => import('./pages/PaymentGatewayPage'));
const ContentModerationPage = lazy(() => import('./pages/ContentModerationPage'));
const LicenseManagerPage = lazy(() => import('./pages/LicenseManagerPage'));
const FeedbackAnalyticsPage = lazy(() => import('./pages/FeedbackAnalyticsPage'));
// R38
const ProfitCenterPage = lazy(() => import('./pages/ProfitCenterPage'));
const SprintVelocityPage = lazy(() => import('./pages/SprintVelocityPage'));
const AccessLogPage = lazy(() => import('./pages/AccessLogPage'));
// Raporty i listy kontrolne — wspólny moduł `rap_*` (docs/RAPORTY-I-LISTY-KONTROLNE-MASTERPROMPT.md)
const RaportyPage = lazy(() => import('./pages/RaportyPage'));
const NpsEnginePage = lazy(() => import('./pages/NpsEnginePage'));
const EmailDripCampaignPage = lazy(() => import('./pages/EmailDripCampaignPage'));
const DepartmentBudgetPage = lazy(() => import('./pages/DepartmentBudgetPage'));
const QaTestSuitePage = lazy(() => import('./pages/QaTestSuitePage'));
const SalesPipelinePage = lazy(() => import('./pages/SalesPipelinePage'));
const DataBackupMonitorPage = lazy(() => import('./pages/DataBackupMonitorPage'));
const ClientSurveyEnginePage = lazy(() => import('./pages/ClientSurveyEnginePage'));
// R39
const TaxCalculatorPage = lazy(() => import('./pages/TaxCalculatorPage'));
const EmployeeHandbookPage = lazy(() => import('./pages/EmployeeHandbookPage'));
const ItAssetDiscoveryPage = lazy(() => import('./pages/ItAssetDiscoveryPage'));
const MeetingFeedbackPage = lazy(() => import('./pages/MeetingFeedbackPage'));
const ComplianceTrainingLogPage = lazy(() => import('./pages/ComplianceTrainingLogPage'));
const ClientCommunicationHubPage = lazy(() => import('./pages/ClientCommunicationHubPage'));
// R40
const CashFlowForecastPage = lazy(() => import('./pages/CashFlowForecastPage'));
const EmployeeSurveyPage = lazy(() => import('./pages/EmployeeSurveyPage'));
const LessonsLearnedPage = lazy(() => import('./pages/LessonsLearnedPage'));
const VendorContractPage = lazy(() => import('./pages/VendorContractPage'));
const DatabaseHealthPage = lazy(() => import('./pages/DatabaseHealthPage'));
const ClientReferralPage = lazy(() => import('./pages/ClientReferralPage'));
// R41
const InvoiceReconciliationPage = lazy(() => import('./pages/InvoiceReconciliationPage'));
const EmployeeTimesheetPage = lazy(() => import('./pages/EmployeeTimesheetPage'));
const ProductRoadmapPage = lazy(() => import('./pages/ProductRoadmapPage'));
const ServiceMetricsPage = lazy(() => import('./pages/ServiceMetricsPage'));
const CustomerLifecyclePage = lazy(() => import('./pages/CustomerLifecyclePage'));
const VendorPaymentPage = lazy(() => import('./pages/VendorPaymentPage'));
const DocumentClassificationPage = lazy(() => import('./pages/DocumentClassificationPage'));
const ProfitLossReportPage = lazy(() => import('./pages/ProfitLossReportPage'));
const ApiVersionManagerPage = lazy(() => import('./pages/ApiVersionManagerPage'));
const TeamCapacityPage = lazy(() => import('./pages/TeamCapacityPage'));
// R42 MEGA
const AiCommandCenterPage = lazy(() => import('./pages/AiCommandCenterPage'));
const LiveOpsWallPage = lazy(() => import('./pages/LiveOpsWallPage'));
const StrategicPlannerPage = lazy(() => import('./pages/StrategicPlannerPage'));
const ExecutiveBriefingPage = lazy(() => import('./pages/ExecutiveBriefingPage'));
const SmartNotificationsEnginePage = lazy(() => import('./pages/SmartNotificationsEnginePage'));
const Client360ViewPage = lazy(() => import('./pages/Client360ViewPage'));
const ProcessMiningPage = lazy(() => import('./pages/ProcessMiningPage'));
const BusinessRulesEnginePage = lazy(() => import('./pages/BusinessRulesEnginePage'));
const RealTimeAnalyticsPage = lazy(() => import('./pages/RealTimeAnalyticsPage'));
const PlatformHealthScorePage = lazy(() => import('./pages/PlatformHealthScorePage'));
// Coming Soon — displayed for sidebar items without dedicated pages
const ComingSoonPage: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="text-6xl mb-4">🚧</div>
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Moduł w przygotowaniu</h1>
      <p className="text-gray-500 mb-4">Strona <code className="bg-gray-100 px-2 py-0.5 rounded text-sm">{pathname}</code> jest w budowie.</p>
      <a href="/dashboard" className="text-blue-600 hover:text-blue-800 font-medium">← Wróć do Dashboard</a>
    </div>
  );
};

// Smart redirect: logged in → /crm, not logged in → /login
const RootRedirect: React.FC = () => {
  const token = localStorage.getItem('grant_crm_token');
  return <Navigate to={token ? '/dashboard' : '/login'} replace />;
};

// External redirect (full URL)
const ExternalRedirect: React.FC<{ to: string }> = ({ to }) => {
  window.location.href = to;
  return null;
};

// Legacy redirect with param interpolation
const LegacyApplicationRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/crm/application/${id}`} replace />;
};

// Legacy /applications?id=X redirect
const LegacyApplicationsQueryRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  if (id) return <Navigate to={`/crm/application/${id}`} replace />;
  return <Navigate to="/crm" replace />;
};

function App() {
  return (
    <YearProvider>
    <CompanyProvider>
    <ToastProvider>
    <ConfirmProvider>
      <Router>
        <ErrorBoundary>
        <Suspense fallback={<LazyFallback />}>
        <Routes>
          {/* Auth Routes (Public) */}
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Root: smart redirect (no public landing page) */}
          <Route path="/" element={<RootRedirect />} />

          {/* Public Application Forms — wrapped in ErrorBoundary */}
          <Route path="/crm/apply" element={<ErrorBoundary><CRMApplicationForm /></ErrorBoundary>} />
          <Route path="/apply" element={<Navigate to="/crm/apply" replace />} />
          <Route path="/wsparcie/apply" element={<ErrorBoundary><WsparcieApplicationForm /></ErrorBoundary>} />
          <Route path="/kfs/apply" element={<ErrorBoundary><KFSApplicationForm /></ErrorBoundary>} />
          <Route path="/zus/apply" element={<ErrorBoundary><ZUSApplicationForm /></ErrorBoundary>} />
          <Route path="/bhp/apply" element={<ErrorBoundary><BHPApplicationForm /></ErrorBoundary>} />
          <Route path="/pozyczki-rozwojowe/apply" element={<ErrorBoundary><PozyczkaRozwojowaForm /></ErrorBoundary>} />
          <Route path="/dotacje-rozwojowe/apply" element={<ErrorBoundary><DotacjeRozwojoweForm /></ErrorBoundary>} />
          {/* Hutnik Demo Pages */}
          <Route path="/hutnik-demo/dashboard" element={<HutnikB2BDashboard />} />
          <Route path="/hutnik-demo/stadion" element={<StadiumVisionLanding />} />
          <Route path="/hutnik-demo/karta-kibica" element={<FanMemberCard />} />
          <Route path="/granty-fpn/zglos" element={<ErrorBoundary><GrantFpnPublicForm /></ErrorBoundary>} />
          <Route path="/szkolenia/apply" element={<ErrorBoundary><SzkoleniaApplicationForm /></ErrorBoundary>} />
          <Route path="/turnusy/apply" element={<ErrorBoundary><TurnusyApplicationForm /></ErrorBoundary>} />
          <Route path="/wycieczki/apply" element={<ErrorBoundary><WycieczkiForm /></ErrorBoundary>} />
          <Route path="/czyste-powietrze/apply" element={<ErrorBoundary><CzystePowietrzeApplicationForm /></ErrorBoundary>} />
          <Route path="/ksiegowosc/apply" element={<ErrorBoundary><UslugaKsiegowaApplicationForm /></ErrorBoundary>} />
          <Route path="/marketing-crm/apply" element={<ErrorBoundary><GenericApplicationForm serviceType="marketing_beauty" /></ErrorBoundary>} />
          <Route path="/gastro/apply" element={<ErrorBoundary><GenericApplicationForm serviceType="gastro_consulting" /></ErrorBoundary>} />
          <Route path="/steam-crm/apply" element={<ErrorBoundary><GenericApplicationForm serviceType="steam_education" /></ErrorBoundary>} />

          {/* Contest — public pages */}
          <Route path="/konkurs" element={<ErrorBoundary><ContestPage /></ErrorBoundary>} />
          <Route path="/konkurs/ranking" element={<ErrorBoundary><ContestLeaderboardPage /></ErrorBoundary>} />

          {/* Public Grant Map & Search */}
          <Route path="/mapa-operatorow" element={<ErrorBoundary><PublicGrantMapPage /></ErrorBoundary>} />
          <Route path="/wyszukiwarka-dotacji" element={<ErrorBoundary><PublicGrantSearchPage /></ErrorBoundary>} />

          {/* Client Portal (Public - token-based access) */}
          <Route path="/portal" element={<ErrorBoundary><Suspense fallback={<div className="flex justify-center py-24"><Loader2 className="animate-spin" size={32} /></div>}><PortalLoginPage /></Suspense></ErrorBoundary>} />
          <Route path="/portal/:token" element={<ErrorBoundary><ClientPortalPage /></ErrorBoundary>} />
          <Route path="/portal/v2/:token" element={<ErrorBoundary><Suspense fallback={<div className="flex justify-center py-24"><Loader2 className="animate-spin" size={32} /></div>}><ClientPortalV2 /></Suspense></ErrorBoundary>} />

          {/* Hosting Renewal (Public - token-based Stripe payment) */}
          <Route path="/hosting/renew/:token" element={<ErrorBoundary><Suspense fallback={<div className="flex justify-center py-24"><Loader2 className="animate-spin" size={32} /></div>}><HostingRenewalPage /></Suspense></ErrorBoundary>} />

          {/* Contract Acceptance (Public - token-based) */}
          <Route path="/accept/:token" element={<ErrorBoundary><ContractAcceptancePage /></ErrorBoundary>} />

          {/* Protected CRM Routes with Sidebar Layout */}
          <Route
            element={
              <ErrorBoundary>
              <ProtectedRoute requiredRoles={['ADMIN', 'OPERATOR']}>
                <ProductProvider>
                  <AppLayout />
                </ProductProvider>
              </ProtectedRoute>
              </ErrorBoundary>
            }
          >
            {/* Global Dashboard */}
            <Route path="/dashboard" element={<GlobalDashboard />} />

            {/* CRM Product Kanbans — key forces full remount on navigation */}
            <Route path="/crm" element={<ProductModuleLayout productCode="pup_grant" />} />
            <Route path="/crm/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/crm/:tab" element={<ProductModuleWithTab productCode="pup_grant" />} />
            <Route path="/wsparcie" element={<ProductModuleLayout productCode="wsparcie_na_starcie" />} />
            <Route path="/wsparcie/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/wsparcie/:tab" element={<ProductModuleWithTab productCode="wsparcie_na_starcie" />} />
            <Route path="/kfs" element={<ProductModuleLayout productCode="kfs" />} />
            <Route path="/kfs/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/zus" element={<ProductModuleLayout productCode="zus" />} />
            <Route path="/zus/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/bhp" element={<ProductModuleLayout productCode="bhp" />} />
            <Route path="/bhp/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/szkolenia" element={<UniversalKanban key="szkolenia" productCode="szkolenia" />} />
            <Route path="/szkolenia/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/turnusy" element={<UniversalKanban key="turnusy" productCode="turnusy" />} />
            <Route path="/turnusy/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/turnusy/dashboard" element={<TurnusyDashboard />} />
            <Route path="/turnusy/klienci" element={<TurnusyClientList />} />
            <Route path="/turnusy/klienci/:id" element={<ClientProfile360 />} />
            <Route path="/turnusy/osrodki" element={<TurnusyCenterList />} />
            <Route path="/turnusy/osrodki/:id" element={<TurnusyCenterDetail />} />
            <Route path="/turnusy/turnusy" element={<TurnusyTripList />} />
            <Route path="/turnusy/rezerwacje" element={<TurnusyBookingList />} />
            <Route path="/turnusy/rezerwacje/:id" element={<TurnusyBookingDetail />} />
            <Route path="/turnusy/dopasowanie" element={<TurnusyMatchingPanel />} />
            <Route path="/turnusy/pokoje" element={<RoomAllocationMatrix />} />
            <Route path="/turnusy/rezerwacje-tabela" element={<TurnusBookingsTable />} />
            <Route path="/turnusy/raporty" element={<TurnusyReportsPage />} />
            <Route path="/turnusy/organizatorzy" element={<TurnusyOrganizerList />} />
            <Route path="/turnusy/kampanie" element={<TurnusyCampaignList />} />
            <Route path="/turnusy/import-empatia" element={<EmpatiaImportPanel />} />
            <Route path="/turnusy/wzbogacanie" element={<TurnusyBatchEnrichmentPage />} />
            <Route path="/turnusy/cms" element={<TurnusyCmsPanel />} />
            <Route path="/turnusy/pipeline" element={<CooperationPipeline />} />
            <Route path="/turnusy/revenue" element={<TurnusyRevenueDashboard />} />

            {/* EduGrantOS: Trainings module */}
            <Route path="/kalendarz" element={<UnifiedCalendarPage />} />
            <Route path="/szkolenia-modul" element={<TrainingsDashboard />} />
            <Route path="/szkolenia-modul/kalendarz" element={<TrainingCalendar />} />
            <Route path="/szkolenia-modul/sesje" element={<SessionsList />} />
            <Route path="/szkolenia-modul/sesja/:id" element={<SessionDetails />} />
            <Route path="/szkolenia-modul/kursy" element={<CoursesManagement />} />
            <Route path="/szkolenia-modul/trenerzy" element={<TrainersManagement />} />
            <Route path="/szkolenia-modul/uczestnicy" element={<ParticipantsManagement />} />
            <Route path="/szkolenia-modul/firmy" element={<CompaniesManagement />} />
            <Route path="/szkolenia-modul/bur" element={<BurIntegrationPanel />} />
            <Route path="/szkolenia-modul/kurs/:id" element={<CourseDetail />} />
            <Route path="/szkolenia-modul/trener/:id" element={<TrainerDetail />} />
            <Route path="/szkolenia-modul/raporty" element={<TrainingReportsPage />} />
            <Route path="/szkolenia-modul/zasoby" element={<ResourcesManagement />} />
            <Route path="/szkolenia-modul/testy" element={<TestsManagement />} />
            <Route path="/szkolenia-modul/gf-courses" element={<GFCoursesManagement />} />
            <Route path="/szkolenia-modul/kfs" element={<KFSManagement />} />

            <Route path="/wycieczki" element={<ProductModuleLayout productCode="wycieczki" />} />
            <Route path="/wycieczki/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/wycieczki/:tab" element={<ProductModuleWithTab productCode="wycieczki" />} />
            <Route path="/autoestetyka" element={<ProductModuleLayout productCode="autoestetyka" />} />
            <Route path="/autoestetyka/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/autoestetyka/:tab" element={<ProductModuleWithTab productCode="autoestetyka" />} />
            <Route path="/pozyczki-rozwojowe" element={<ProductModuleLayout productCode="pozyczki_rozwojowe" />} />
            <Route path="/pozyczki-rozwojowe/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/pozyczki-rozwojowe/:tab" element={<ProductModuleWithTab productCode="pozyczki_rozwojowe" />} />
            <Route path="/dotacje-rozwojowe" element={<UniversalKanban key="dotacje_rozwojowe" productCode="dotacje_rozwojowe" />} />
            <Route path="/dotacje-rozwojowe/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/dotacje-rozwojowe/nabory" element={<GrantCallsPage />} />
            <Route path="/dotacje-rozwojowe/matching" element={<GrantMatchingPage />} />
            <Route path="/dotacje-rozwojowe/rozliczenia" element={<GrantSettlementsPage />} />
            <Route path="/dotacje-rozwojowe/operatorzy" element={<GrantOperatorsPage />} />
            <Route path="/dotacje-rozwojowe/scrape-monitor" element={<ScrapeMonitorPage />} />
            <Route path="/dotacje-rozwojowe/mapa" element={<GrantMapPage />} />
            <Route path="/czyste-powietrze" element={<ProductModuleLayout productCode="czyste_powietrze" />} />
            <Route path="/czyste-powietrze/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/ksiegowosc" element={<ProductModuleLayout productCode="uslugi_ksiegowe" />} />
            <Route path="/ksiegowosc/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/marketing-crm" element={<ProductModuleLayout productCode="marketing_beauty" />} />
            <Route path="/marketing-crm/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/gastro" element={<ProductModuleLayout productCode="gastro_consulting" />} />
            <Route path="/gastro/application/:id" element={<CRMApplicationDetail />} />

            {/* Hutnik Business Club — CRM sponsorów */}
            <Route path="/hutnik-crm" element={<ProductModuleLayout productCode="hutnik_biznes" />} />
            <Route path="/hutnik-crm/application/:id" element={<CRMApplicationDetail />} />

            {/* STEAM Academia — CRM szkół */}
            <Route path="/steam-crm" element={<ProductModuleLayout productCode="steam_education" />} />
            <Route path="/steam-crm/application/:id" element={<CRMApplicationDetail />} />

            {/* Granty FPN — wewnętrzne projekty grantowe Fundacji */}
            <Route path="/granty-fpn" element={<UniversalKanban key="granty_fpn" productCode="granty_fpn" />} />
            <Route path="/granty-fpn/application/:id" element={<CRMApplicationDetail />} />
            <Route path="/granty-fpn/nowy" element={<GrantFpnForm />} />
            <Route path="/granty-fpn/wiedza" element={<GrantKnowledgePage />} />

            {/* Foundation modules */}
            <Route path="/fundacja/projekty" element={<FoundationProjectsPage />} />
            <Route path="/fundacja/projekty/:id" element={<FoundationProjectDetail />} />
            <Route path="/fundacja/darowizny" element={<FoundationDonationsPage />} />
            <Route path="/fundacja/ksiegowosc" element={<FoundationAccountingPage />} />
            <Route path="/fundacja/sprawozdania" element={<FoundationReportsPage />} />
            <Route path="/fundacja/website" element={<FoundationWebsiteManager />} />
            <Route path="/wycieczki/zestawienia" element={<TripFinancialPage />} />
            <Route path="/trip-financial" element={<TripFinancialPage />} />
            <Route path="/wycieczki/terminy" element={<WycieczkiTermsPage />} />
            <Route path="/wycieczki/wplaty" element={<WycieczkiWplatyPage />} />
            <Route path="/open-banking/callback" element={<OpenBankingCallbackPage />} />

            {/* Knowledge Base */}
            <Route path="/crm/knowledge-base" element={<KnowledgeBase />} />

            {/* Tools (extracted from AdminPanel) */}
            <Route path="/sms" element={<AdminPanel initialTab="sms" />} />
            <Route path="/wycieczki-email" element={<AdminPanel initialTab="wycieczki-email" />} />
            <Route path="/email-templates" element={<EmailTemplatesPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/payments" element={<PaymentTrackingPage />} />
            <Route path="/ksef" element={<KSeFPage />} />
            <Route path="/rodo" element={<RodoCompliancePage />} />
            <Route path="/notification-center" element={<NotificationCenterPage />} />
            <Route path="/notification-rules" element={<NotificationRulesPage />} />
            <Route path="/ai-scoring" element={<AIScoringPage />} />
            <Route path="/appointments" element={<AppointmentsPage />} />
            <Route path="/operator-performance" element={<OperatorPerformancePage />} />
            <Route path="/csat" element={<CSATTrackerPage />} />
            <Route path="/document-templates" element={<DocumentTemplatesPage />} />
            <Route path="/client-timeline" element={<ClientTimelinePage />} />
            <Route path="/pipeline-forecast" element={<PipelineForecastPage />} />
            <Route path="/sla-monitor" element={<SLAMonitorPage />} />
            <Route path="/sla-dashboard" element={<SLADashboardPage />} />
            <Route path="/grant-calculator" element={<GrantCalculatorPage />} />
            <Route path="/daily-digest" element={<DailyDigestPage />} />
            <Route path="/contractors" element={<ContractorsPage />} />
            <Route path="/heatmap" element={<HeatmapPage />} />
            <Route path="/export-center" element={<ExportCenterPage />} />
            <Route path="/project-budget" element={<ProjectBudgetPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/feedback-surveys" element={<FeedbackSurveyPage />} />
            <Route path="/feedback-wall" element={<FeedbackWallPage />} />
            <Route path="/okr" element={<OKRGoalsPage />} />
            <Route path="/contract-registry" element={<ContractRegistryPage />} />
            <Route path="/clm" element={<ContractLifecyclePage />} />
            <Route path="/finance" element={<FinanceDashboardPage />} />
            <Route path="/revenue-forecast" element={<RevenueForecastPage />} />
            <Route path="/wiki" element={<InternalWikiPage />} />
            <Route path="/kb" element={<KnowledgeBaseV2Page />} />
            <Route path="/assets" element={<AssetManagementPage />} />
            <Route path="/brand-assets" element={<BrandAssetsPage />} />
            <Route path="/announcements" element={<AnnouncementsPage />} />
            <Route path="/roi" element={<ROICalculatorPage />} />
            <Route path="/compliance" element={<ComplianceChecklistPage />} />
            <Route path="/qa" element={<QualityAssurancePage />} />
            <Route path="/policies" element={<PolicyManagerPage />} />
            <Route path="/segments" element={<ClientSegmentsPage />} />
            <Route path="/activity-log" element={<ActivityLogPage />} />
            <Route path="/audit" element={<AuditTrailPage />} />
            <Route path="/task-board" element={<TaskBoardPage />} />
            <Route path="/email-automation" element={<EmailAutomationPage />} />
            <Route path="/cost-tracker" element={<CostTrackerPage />} />
            <Route path="/time-tracking" element={<TimeTrackingPage />} />
            <Route path="/helpdesk" element={<HelpdeskPage />} />
            <Route path="/sms-gateway" element={<SmsGatewayPage />} />
            <Route path="/commissions" element={<CommissionsPage />} />
            <Route path="/referrals" element={<ReferralProgramPage />} />
            <Route path="/subscriptions" element={<SubscriptionManagerPage />} />
            <Route path="/service-catalog" element={<ServiceCatalogPage />} />
            <Route path="/proposals" element={<ProposalGeneratorPage />} />
            <Route path="/risk-register" element={<RiskRegisterPage />} />
            <Route path="/risk-assessment" element={<RiskAssessmentPage />} />
            <Route path="/client-360" element={<Client360Page />} />
            <Route path="/internal-notifications" element={<InternalNotificationsPage />} />
            <Route path="/report-generator" element={<ReportGeneratorPage />} />
            <Route path="/e-signature" element={<ESignaturePage />} />
            <Route path="/saas-inventory" element={<SaasInventoryPage />} />
            <Route path="/vendors" element={<VendorManagementPage />} />
            <Route path="/procurement" element={<ProcurementPage />} />
            <Route path="/expense-claims" element={<ExpenseClaimsPage />} />
            <Route path="/travel-expenses" element={<TravelExpensesPage />} />
            <Route path="/team-kb" element={<TeamKnowledgeBasePage />} />
            <Route path="/meeting-notes" element={<MeetingNotesPage />} />
            <Route path="/office-inventory" element={<OfficeInventoryPage />} />
            <Route path="/data-quality" element={<DataQualityPage />} />
            <Route path="/hosting-tracker" element={<HostingTrackerPage />} />
            <Route path="/custom-forms" element={<CustomFormsPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/project-timeline" element={<ProjectTimelinePage />} />
            <Route path="/surveys" element={<SurveyBuilderPage />} />
            <Route path="/nps" element={<NPSTrackerPage />} />
            <Route path="/goal-tracker" element={<GoalTrackerPage />} />
            <Route path="/shift-scheduler" element={<ShiftSchedulerPage />} />
            <Route path="/absences" element={<AbsenceManagerPage />} />
            <Route path="/fleet" element={<FleetManagementPage />} />
            <Route path="/marketing" element={<AdminPanel initialTab="marketing" />} />
            <Route path="/import" element={<AdminPanel initialTab="import" />} />
            <Route path="/ai-agent" element={<AdminPanel initialTab="ai-agent" />} />
            <Route path="/ai-modules" element={<AIModulesPage />} />
            <Route path="/ai-analysis" element={<CrmAiAnalysisPage />} />
            <Route path="/modules" element={<ModuleBrowserPage />} />
            <Route path="/api-docs" element={<ApiDocsPage />} />
            <Route path="/api-analytics" element={<ApiAnalyticsPage />} />

            {/* System */}
            <Route path="/tenants" element={<TenantManagerPage />} />
            <Route path="/users" element={<UserManagementPage />} />
            <Route path="/admin/audit" element={<AdminPanel initialTab="audit" />} />
            <Route path="/webhooks" element={<AdminPanel initialTab="webhooks" />} />
            <Route path="/backups" element={<BackupManagerPage />} />
            <Route path="/industries" element={<IndustryManagerPage />} />
            <Route path="/cron-jobs" element={<CronJobsPage />} />
            <Route path="/drip-campaigns" element={<Suspense fallback={<LazyFallback />}><LeadNurturingPage /></Suspense>} />
            <Route path="/lead-nurturing" element={<Suspense fallback={<LazyFallback />}><LeadNurturingPage /></Suspense>} />
            <Route path="/clients" element={<ClientsPage />} />

            {/* B2B Enterprise Module */}
            <Route path="/companies" element={<CompaniesPage />} />
            <Route path="/companies/cross-sell" element={<CrossSellPage />} />
            <Route path="/companies/:id" element={<CompanyProfile360 />} />

            {/* Strony klientów — rozliczenie dotacji */}
            <Route path="/websites" element={<ClientWebsitesPage />} />
            <Route path="/retention" element={<RetentionBoardPage />} />
            <Route path="/strony-wsparcie" element={<SupportDashboardPage />} />
            <Route path="/migration" element={<MigrationPanel />} />
            <Route path="/www-strony-klientow" element={<InfrastructureOverviewPage />} />
            <Route path="/monitoring" element={<MonitoringDashboard />} />
            <Route path="/grant-monitor" element={<GrantMonitorPage />} />
            <Route path="/portfel-dotacji" element={<GrantPortfolioPage />} />

            {/* Anioły Biznesu */}
            <Route path="/angels" element={<AngelsPage />} />

            {/* Partnerzy krakow.business */}
            <Route path="/partners" element={<PartnersPage />} />

            {/* B2B Portal — Portal Współpracy */}
            <Route path="/b2b/issues" element={<B2BIssuesPage />} />
            <Route path="/b2b/issues/new" element={<B2BNewIssuePage />} />
            <Route path="/b2b/issues/:id" element={<B2BIssueDetail />} />

            {/* OCR Documents */}
            <Route path="/ocr-documents" element={<OcrDocumentsPage />} />
            <Route path="/ocr-scanner" element={<OcrScannerPage />} />

            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/briefing" element={<OperatorBriefingPage />} />
            <Route path="/cockpit" element={<ExecutiveDashboardPage />} />
            <Route path="/pulpit-fundacji" element={<FoundationCockpitPage />} />
            <Route path="/fundraising" element={<FundraisingDashboardPage />} />
            <Route path="/admin/cms" element={<AdminCMS />} />
            <Route path="/print-queue" element={<PrintQueuePage />} />
            <Route path="/social-media" element={<SocialMediaPage />} />
            <Route path="/newsletter-digest" element={<NewsletterDigestPage />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/mailing" element={<MailingPage />} />
            <Route path="/mailing/new" element={<MailingEditorPage />} />
            <Route path="/mailing/:id" element={<MailingDetailPage />} />
            <Route path="/mailing/:id/edit" element={<MailingEditorPage />} />
            <Route path="/mail-merge" element={<MailMergePage />} />
            <Route path="/admin/panel" element={<AdminPanel />} />
            <Route path="/volunteer-certificates" element={<VolunteerCertificatesPage />} />
            <Route path="/zaswiadczenia-wolontariat" element={<VolunteerCertificatesPage />} />
            <Route path="/wolontariat" element={<VolunteerRegistryPage />} />
            <Route path="/ewidencja-wolontariatu" element={<VolunteerRegistryPage />} />
            <Route path="/admin/wycieczki-email" element={<AdminPanel initialTab="wycieczki-email" />} />
            <Route path="/admin/email-templates" element={<AdminPanel initialTab="email-templates" />} />
            <Route path="/admin/drip-campaigns" element={<AdminPanel initialTab="drip-campaigns" />} />
            <Route path="/admin/cron" element={<AdminPanel initialTab="cron" />} />
            <Route path="/admin/duplicates" element={<DuplicatesPage />} />
            <Route path="/client-scoring" element={<ClientScoringPage />} />
            <Route path="/client-health" element={<ClientHealthPage />} />
            <Route path="/learning-progress" element={<LearningProgressPage />} />
            <Route path="/visitors" element={<VisitorManagementPage />} />
            <Route path="/tags" element={<TagsManagerPage />} />
            <Route path="/center-enrichment" element={<CenterEnrichmentPage />} />
            <Route path="/workflow" element={<WorkflowBuilderPage />} />
            <Route path="/webhook-manager" element={<WebhookManagerPage />} />
            <Route path="/document-workflow" element={<DocumentWorkflowPage />} />
            <Route path="/documents-manager" element={<DocumentsManagerPage />} />
            <Route path="/weekly-reports" element={<WeeklyReportsPage />} />
            <Route path="/smart-documents" element={<SmartDocumentsPage />} />
            <Route path="/bulk-operations" element={<BulkOperationsPage />} />
            <Route path="/integrations" element={<IntegrationHubPage />} />
            <Route path="/admin/google-workspace" element={<GoogleWorkspaceManager />} />
            <Route path="/google-workspace" element={<GoogleWorkspaceAdminPage />} />
            <Route path="/google-integrations" element={<GoogleIntegrationsPage />} />
            <Route path="/google-drive" element={<GoogleDrivePage />} />

            {/* HR & PES */}
            <Route path="/team-members" element={<TeamMembersPage />} />
            <Route path="/pes-support" element={<PESSupportPage />} />
            <Route path="/edoreczenia" element={<EDoreczeniaPage />} />
            <Route path="/cloudflare-dns" element={<CloudflareDNS />} />
            <Route path="/email" element={<EmailInboxPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />

            {/* Sklep Nowohucki */}
            <Route path="/sklep" element={<SklepPage />} />

            {/* SINCE 1950 */}
            <Route path="/since" element={<SincePage />} />

            {/* KFS Panel */}
            <Route path="/kfs-dashboard" element={<KfsDashboardHub />} />
            <Route path="/kfs-panel" element={<KfsPanelPage />} />
            <Route path="/kfs-handlowiec" element={<KfsHandlowiecDashboard />} />
            <Route path="/kfs-power" element={<KfsPowerPanel />} />
            <Route path="/kfs-kanban" element={<KfsKanbanBoard />} />
            <Route path="/kfs-email" element={<KfsEmailComposer />} />
            <Route path="/kfs-import" element={<KfsLeadImport />} />
            <Route path="/kfs-magic-import" element={<KfsExcelImportPage />} />
            <Route path="/kfs-notifications" element={<KfsNotifications />} />
            <Route path="/kfs-automation" element={<KfsAutomationRules />} />
            <Route path="/kfs-pup-timeline" element={<KfsPupTimeline />} />
            <Route path="/kfs-activity" element={<KfsActivityStream />} />
            <Route path="/kfs-performance" element={<KfsPerformanceTracker />} />
            <Route path="/kfs-scoring" element={<KfsScoringTuner />} />
            <Route path="/kfs-compliance" element={<KfsComplianceOverview />} />
            <Route path="/kfs-enrollment" element={<KfsEnrollmentBridge />} />
            <Route path="/kfs-company/:id" element={<KfsCompany360 />} />
            <Route path="/kfs-scheduler" element={<KfsSmartScheduler />} />
            <Route path="/kfs-sequences" element={<KfsFollowUpSequences />} />
            <Route path="/kfs-territory" element={<KfsTerritoryMap />} />
            <Route path="/kfs-bulk" element={<KfsBulkActions />} />
            <Route path="/kfs-proposals" element={<KfsProposalGenerator />} />
            <Route path="/kfs-meetings" element={<KfsMeetingNotes />} />
            <Route path="/kfs-pipeline-analytics" element={<KfsPipelineAnalytics />} />
            <Route path="/kfs-documents" element={<KfsDocumentTemplates />} />
            <Route path="/kfs-integrations" element={<KfsIntegrationHub />} />
            <Route path="/kfs-goals" element={<KfsGoalTracker />} />
            <Route path="/kfs-competitors" element={<KfsCompetitorAnalysis />} />
            <Route path="/kfs-client-portal" element={<KfsClientPortal />} />
            <Route path="/kfs-catalog" element={<KfsTrainingCatalog />} />
            <Route path="/kfs-roi" element={<KfsROICalculator />} />
            <Route path="/kfs-sms" element={<KfsSmsComposer />} />
            <Route path="/kfs-tasks" element={<KfsTaskManager />} />
            <Route path="/kfs-qualification" element={<KfsQualificationWizard />} />
            <Route path="/kfs-referrals" element={<KfsReferralProgram />} />
            <Route path="/kfs-reports" element={<KfsReportingDashboard />} />
            <Route path="/kfs-contracts" element={<KfsContractManager />} />
            <Route path="/kfs-financial" element={<KfsFinancialOverview />} />
            <Route path="/kfs-calendar-view" element={<KfsTrainingCalendarView />} />
            <Route path="/kfs-team-workload" element={<KfsTeamWorkload />} />
            <Route path="/kfs-funnel" element={<KfsFunnelOptimizer />} />
            <Route path="/kfs-document-vault" element={<KfsDocumentVault />} />
            <Route path="/kfs-lead-timeline/:leadId?" element={<KfsLeadTimeline />} />
            <Route path="/kfs-nabor-comparison" element={<KfsNaborComparison />} />
            <Route path="/kfs-email-tracker" element={<KfsEmailTracker />} />
            <Route path="/kfs-region-heatmap" element={<KfsRegionHeatmap />} />
            <Route path="/kfs-win-loss" element={<KfsWinLossAnalysis />} />
            <Route path="/kfs-contact-log" element={<KfsContactLog />} />
            <Route path="/kfs-budget" element={<KfsBudgetPlanner />} />
            <Route path="/kfs-scorecard/:leadId?" element={<KfsLeadScorecard />} />
            <Route path="/kfs-campaigns" element={<KfsCampaignManager />} />
            <Route path="/kfs-invoices" element={<KfsInvoiceTracker />} />
            <Route path="/kfs-trainers-dir" element={<KfsTrainerDirectory />} />
            <Route path="/kfs-pup-directory" element={<KfsPupDirectory />} />
            <Route path="/kfs-deduplicator" element={<KfsLeadDeduplicator />} />
            <Route path="/kfs-success-stories" element={<KfsSuccessStories />} />
            <Route path="/kfs-renewals" element={<KfsRenewalTracker />} />
            <Route path="/kfs-competitor-intel" element={<KfsCompetitorIntel />} />
            <Route path="/kfs-roi-calculator" element={<KfsROICalculator />} />
            <Route path="/kfs-application-wizard" element={<KfsApplicationWizard />} />
            <Route path="/kfs-partner-network" element={<KfsPartnerNetwork />} />
            <Route path="/kfs-market-analysis" element={<KfsMarketAnalysis />} />
            <Route path="/kfs-doc-generator" element={<KfsDocumentGenerator />} />
            <Route path="/kfs-meeting-scheduler" element={<KfsMeetingScheduler />} />
            <Route path="/kfs-ab-tests" element={<KfsABTestTracker />} />
            <Route path="/kfs-churn-predictor" element={<KfsChurnPredictor />} />
            <Route path="/kfs-surveys" element={<KfsSatisfactionSurvey />} />
            <Route path="/kfs-quotations" element={<KfsQuotationBuilder />} />
            <Route path="/kfs-webhooks" element={<KfsWebhookManager />} />
            <Route path="/kfs-nabor-calendar" element={<KfsNaborCalendar />} />
            <Route path="/kfs-company-profiles" element={<KfsCompanyProfile />} />
            <Route path="/kfs-sales-scripts" element={<KfsSalesScript />} />
            <Route path="/kfs-events" element={<KfsEventCalendar />} />
            <Route path="/kfs-audit-log" element={<KfsAuditLog />} />
            <Route path="/kfs-pricing" element={<KfsPricingStrategy />} />
            <Route path="/kfs-client-feedback" element={<KfsClientFeedback />} />
            <Route path="/kfs-certificates" element={<KfsCertificateManager />} />
            <Route path="/kfs-trainers" element={<KfsTrainerManagement />} />
            <Route path="/kfs-revenue-forecast" element={<KfsRevenueForecaster />} />
            <Route path="/kfs-email-sequences" element={<KfsEmailSequence />} />
            <Route path="/kfs-data-export" element={<KfsDataExport />} />
            <Route path="/kfs-pup-relationships" element={<KfsPupRelationship />} />
            <Route path="/kfs-onboarding" element={<KfsOnboarding />} />
            <Route path="/kfs-doc-checklist" element={<KfsDocumentChecklist />} />
            <Route path="/kfs-compliance-calendar" element={<KfsComplianceCalendar />} />
            <Route path="/kfs-team-perf" element={<KfsTeamPerformanceDash />} />
            <Route path="/kfs-marketing-campaigns" element={<KfsMarketingCampaign />} />
            <Route path="/kfs-call-log" element={<KfsVoipLogger />} />
            <Route path="/kfs-content-library" element={<KfsContentLibrary />} />
            <Route path="/kfs-deadlines" element={<KfsDeadlineTracker />} />
            <Route path="/kfs-custom-reports" element={<KfsCustomReports />} />
            <Route path="/kfs-workflows" element={<KfsWorkflowBuilder />} />
            <Route path="/kfs-executive" element={<KfsExecutiveDashboard />} />
            <Route path="/kfs-leads" element={<KfsLeadDashboard />} />
            <Route path="/kfs-programs" element={<KfsTrainingProgramsPage />} />

            {/* Contest Admin */}
            <Route path="/contest-admin" element={<ContestAdminPage />} />
            {/* CEBRO Admin */}
            <Route path="/cebro-admin" element={<CebroAdminPage />} />
            {/* GrantOS Admin Center */}
            <Route path="/admin-center" element={<AdminCenterPage />} />

            {/* Client Onboarding */}
            <Route path="/onboarding" element={<ClientOnboardingPage />} />

            {/* Customer Journey */}
            <Route path="/customer-journey" element={<CustomerJourneyPage />} />

            {/* Sprint Board */}
            <Route path="/sprints" element={<SprintBoardPage />} />

            {/* Analytics */}
            <Route path="/analytics" element={<AnalyticsDashboardPage />} />
            <Route path="/bi-dashboard" element={<BusinessIntelligencePage />} />
            <Route path="/knowledge-graph" element={<KnowledgeGraphPage />} />
            <Route path="/competitor-tracker" element={<CompetitorTrackerPage />} />

            {/* Incident Management */}
            <Route path="/incidents" element={<IncidentManagementPage />} />

            {/* Resource Planner */}
            <Route path="/resource-planner" element={<ResourcePlannerPage />} />
            <Route path="/capacity-forecast" element={<CapacityForecastPage />} />

            {/* Event Manager */}
            <Route path="/events" element={<EventManagerPage />} />

            {/* Inventory / Stock Management */}
            <Route path="/inventory" element={<InventoryManagementPage />} />

            {/* Meeting Rooms — conference room booking */}
            <Route path="/meeting-rooms" element={<Suspense fallback={<LazyFallback />}><MeetingRoomsPage /></Suspense>} />

            {/* Change Request Management -- ITIL RFC */}
            <Route path="/change-requests" element={<ChangeRequestsPage />} />

            {/* Document Scanner / File Analyzer */}
            <Route path="/doc-scanner" element={<Suspense fallback={<LazyFallback />}><DocumentScannerPage /></Suspense>} />

            {/* R16 modules */}
            <Route path="/product-catalog" element={<ProductCatalogPage />} />
            <Route path="/testimonials" element={<TestimonialManagerPage />} />
            <Route path="/campaign-roi" element={<CampaignROIPage />} />
            <Route path="/employee-directory" element={<EmployeeDirectoryPage />} />
            <Route path="/ideas" element={<IdeaBoardPage />} />
            <Route path="/certifications" element={<CertificationTrackerPage />} />
            <Route path="/quizzes" element={<KnowledgeQuizPage />} />
            <Route path="/budget-planner" element={<BudgetPlannerPage />} />
            <Route path="/vendor-scorecard" element={<VendorScorecardPage />} />
            <Route path="/whiteboards" element={<WhiteboardPage />} />

            {/* R17 modules */}
            <Route path="/visitor-log" element={<VisitorLogPage />} />
            <Route path="/payroll" element={<PayrollOverviewPage />} />
            <Route path="/document-archive" element={<DocumentArchivePage />} />
            <Route path="/equipment-maintenance" element={<EquipmentMaintenancePage />} />
            <Route path="/release-notes" element={<ReleaseNotesPage />} />
            <Route path="/service-desk" element={<ServiceDeskPage />} />
            <Route path="/workspace-booking" element={<WorkspaceBookingPage />} />
            <Route path="/knowledge-faq" element={<KnowledgeFaqPage />} />
            <Route path="/compliance-training" element={<ComplianceTrainingPage />} />

            {/* R18 modules */}
            <Route path="/portal-config" element={<PortalConfigPage />} />
            <Route path="/template-library" element={<TemplateLibraryPage />} />
            <Route path="/cost-centers" element={<CostCenterPage />} />
            <Route path="/sop" element={<SOPManagerPage />} />
            <Route path="/communication-log" element={<CommunicationLogPage />} />
            <Route path="/data-import" element={<DataImportPage />} />
            <Route path="/dashboard-builder" element={<DashboardBuilderPage />} />
            <Route path="/access-control" element={<AccessControlPage />} />
            <Route path="/price-list" element={<PriceListPage />} />
            <Route path="/system-health" element={<SystemHealthPage />} />

            {/* R19 modules */}
            <Route path="/onboarding-checklist" element={<OnboardingChecklistPage />} />
            <Route path="/deliveries" element={<DeliveryTrackerPage />} />
            <Route path="/client-contracts" element={<ClientContractsPage />} />
            <Route path="/partner-portal" element={<PartnerPortalPage />} />
            <Route path="/meeting-agenda" element={<MeetingAgendaPage />} />
            <Route path="/asset-lifecycle" element={<AssetLifecyclePage />} />
            <Route path="/approvals" element={<ApprovalWorkflowPage />} />
            <Route path="/mailing-lists" element={<MailingListManagerPage />} />
            <Route path="/legal-cases" element={<LegalCaseTrackerPage />} />
            <Route path="/energy" element={<EnergyMonitorPage />} />

            {/* R20 modules */}
            <Route path="/milestones" element={<ProjectMilestonePage />} />
            <Route path="/standups" element={<DailyStandupPage />} />
            <Route path="/parking" element={<ParkingManagerPage />} />
            <Route path="/business-cards" element={<BusinessCardScannerPage />} />
            <Route path="/office-supplies" element={<OfficeSuppliesPage />} />
            <Route path="/client-feedback-board" element={<ClientFeedbackBoardPage />} />
            <Route path="/training-needs" element={<TrainingNeedsPage />} />
            <Route path="/emergency-contacts" element={<EmergencyContactsPage />} />
            <Route path="/print-labels" element={<PrintLabelsPage />} />
            <Route path="/weather" element={<WeatherWidgetPage />} />

            {/* R21 modules */}
            <Route path="/kpi" element={<KpiDashboardPage />} />
            <Route path="/content-calendar" element={<ContentCalendarPage />} />
            <Route path="/donations-tracker" element={<DonationTrackerPage />} />
            <Route path="/satisfaction" element={<SatisfactionIndexPage />} />
            <Route path="/bookshelf" element={<OfficeBookshelfPage />} />
            <Route path="/team-mood" element={<TeamMoodPage />} />
            <Route path="/benefits" element={<BenefitManagerPage />} />
            <Route path="/server-inventory" element={<ServerInventoryPage />} />
            <Route path="/retrospectives" element={<RetrospectivePage />} />
            <Route path="/password-vault" element={<PasswordVaultPage />} />
            {/* R22 — only truly new routes */}
            <Route path="/visitor-management" element={<VisitorManagementPage />} />
            <Route path="/business-intelligence" element={<BusinessIntelligencePage />} />
            {/* R23 */}
            <Route path="/loyalty" element={<LoyaltyProgramPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/form-analytics" element={<FormAnalyticsPage />} />
            <Route path="/data-export" element={<DataExportPage />} />
            {/* R26 */}
            <Route path="/competitor-analysis" element={<CompetitorAnalysisPage />} />
            <Route path="/churn-predictor" element={<ChurnPredictorPage />} />
            <Route path="/campaign-manager" element={<CampaignManagerPage />} />
            <Route path="/quote-builder" element={<QuoteBuilderPage />} />
            <Route path="/deal-pipeline" element={<DealPipelinePage />} />
            <Route path="/meeting-scheduler" element={<MeetingSchedulerPage />} />
            <Route path="/invoice-generator" element={<InvoiceGeneratorPage />} />
            <Route path="/feedback-collection" element={<FeedbackCollectionPage />} />
            <Route path="/notification-hub" element={<NotificationHubPage />} />
            {/* R27 */}
            <Route path="/workflow-engine" element={<WorkflowEnginePage />} />
            <Route path="/report-builder" element={<ReportBuilderPage />} />
            <Route path="/contact-manager" element={<ContactManagerPage />} />
            <Route path="/product-pricing" element={<ProductPricingPage />} />
            <Route path="/event-scheduler" element={<EventSchedulerPage />} />
            <Route path="/ticket-system" element={<TicketSystemPage />} />
            <Route path="/resource-calendar" element={<ResourceCalendarPage />} />
            <Route path="/expense-manager" element={<ExpenseManagerPage />} />
            <Route path="/document-generator" element={<DocumentGeneratorPage />} />
            <Route path="/activity-feed" element={<ActivityFeedPage />} />
            {/* R28 */}
            <Route path="/sales-forecast" element={<SalesForecastPage />} />
            <Route path="/client-onboarding-flow" element={<ClientOnboardingFlowPage />} />
            <Route path="/performance-reviews" element={<PerformanceReviewPage />} />
            <Route path="/integration-hub" element={<IntegrationHubPage />} />
            <Route path="/task-automation" element={<TaskAutomationPage />} />
            <Route path="/risk-dashboard" element={<RiskDashboardPage />} />
            <Route path="/portal-builder" element={<ClientPortalBuilderPage />} />
            <Route path="/sms-campaigns" element={<SmsCampaignPage />} />
            <Route path="/project-portfolio" element={<ProjectPortfolioPage />} />
            <Route path="/digital-signature" element={<DigitalSignaturePage />} />
            {/* R29 */}
            <Route path="/calendar-sync" element={<CalendarSyncPage />} />
            <Route path="/file-manager" element={<FileManagerPage />} />
            <Route path="/team-directory" element={<TeamDirectoryPage />} />
            <Route path="/client-notes" element={<ClientNotesPage />} />
            <Route path="/training-calendar" element={<TrainingCalendarPage />} />
            <Route path="/vendor-portal" element={<VendorPortalPage />} />
            <Route path="/sla-manager" element={<SlaManagerPage />} />
            <Route path="/contract-templates" element={<ContractTemplatePage />} />
            <Route path="/dashboard-analytics" element={<DashboardAnalyticsPage />} />
            <Route path="/compliance-calendar" element={<ComplianceCalendarPage />} />
            {/* R30 */}
            <Route path="/email-campaigns" element={<EmailCampaignBuilderPage />} />
            <Route path="/feedback-loop" element={<FeedbackLoopPage />} />
            <Route path="/process-modeler" element={<ProcessModelerPage />} />
            <Route path="/data-viz" element={<DataVisualizationPage />} />
            <Route path="/knowledge-hub" element={<KnowledgeHubPage />} />
            <Route path="/user-preferences" element={<UserPreferencesPage />} />
            <Route path="/project-dependencies" element={<ProjectDependenciesPage />} />
            <Route path="/time-off" element={<TimeOffRequestsPage />} />
            <Route path="/notification-templates" element={<NotificationTemplatesPage />} />
            {/* R31 */}
            <Route path="/api-rate-monitor" element={<ApiRateMonitorPage />} />
            <Route path="/client-activity-score" element={<ClientActivityScorePage />} />
            <Route path="/announcement-board" element={<AnnouncementBoardPage />} />
            <Route path="/marketing-funnels" element={<MarketingFunnelPage />} />
            <Route path="/revenue-analytics" element={<RevenueAnalyticsPage />} />
            <Route path="/feature-requests" element={<FeatureRequestBoardPage />} />
            <Route path="/table-browser" element={<TableBrowserPage />} />
            <Route path="/health-checks" element={<HealthCheckDashboardPage />} />
            <Route path="/tag-manager" element={<TagManagerPage />} />
            <Route path="/system-config" element={<SystemConfigPage />} />
            {/* R32 */}
            <Route path="/employee-onboarding" element={<EmployeeOnboardingPage />} />
            <Route path="/service-requests" element={<ServiceRequestPage />} />
            <Route path="/cost-allocation" element={<CostAllocationPage />} />
            <Route path="/client-engagement" element={<ClientEngagementPage />} />
            <Route path="/skills-matrix" element={<SkillsMatrixPage />} />
            <Route path="/document-approval" element={<DocumentApprovalPage />} />
            <Route path="/client-success" element={<ClientSuccessPage />} />
            <Route path="/inventory-alerts" element={<InventoryAlertsPage />} />
            <Route path="/meeting-minutes" element={<MeetingMinutesPage />} />
            <Route path="/ops-dashboard" element={<OpsDashboardPage />} />
            {/* R33 */}
            <Route path="/supply-chain" element={<SupplyChainPage />} />
            <Route path="/employee-recognition" element={<EmployeeRecognitionPage />} />
            <Route path="/capacity-planning" element={<CapacityPlanningPage />} />
            <Route path="/release-management" element={<ReleaseManagementPage />} />
            <Route path="/api-keys" element={<ApiKeyManagerPage />} />
            <Route path="/gantt" element={<GanttChartPage />} />
            <Route path="/currency" element={<MultiCurrencyPage />} />
            <Route path="/client-segmentation" element={<ClientSegmentationPage />} />
            <Route path="/kanban" element={<KanbanBoardPage />} />
            <Route path="/workspace-settings" element={<WorkspaceSettingsPage />} />
            {/* R34 */}
            <Route path="/subscription-billing" element={<SubscriptionBillingPage />} />
            <Route path="/compliance-audit" element={<ComplianceAuditPage />} />
            <Route path="/work-orders" element={<WorkOrderPage />} />
            <Route path="/territories" element={<TerritoryManagerPage />} />
            <Route path="/idea-voting" element={<IdeaVotingPage />} />
            <Route path="/batch-operations" element={<BatchOperationsPage />} />
            <Route path="/client-portfolio" element={<ClientPortfolioPage />} />
            <Route path="/change-log" element={<ChangeLogViewerPage />} />
            <Route path="/scheduled-tasks" element={<ScheduledTasksPage />} />
            {/* R35 */}
            <Route path="/warehouse" element={<WarehouseManagerPage />} />
            <Route path="/contract-negotiation" element={<ContractNegotiationPage />} />
            <Route path="/credit-score" element={<CreditScorePage />} />
            <Route path="/document-versioning" element={<DocumentVersioningPage />} />
            <Route path="/forecasting" element={<ForecastingEnginePage />} />
            <Route path="/policy-compliance" element={<PolicyCompliancePage />} />
            <Route path="/tenant-billing" element={<TenantBillingPage />} />
            <Route path="/win-loss" element={<WinLossAnalysisPage />} />
            <Route path="/data-dictionary" element={<DataDictionaryPage />} />
            <Route path="/ops-playbook" element={<OpsPlaybookPage />} />
            {/* R36 */}
            <Route path="/commission-calculator" element={<CommissionCalculatorPage />} />
            <Route path="/project-risk-matrix" element={<ProjectRiskMatrixPage />} />
            <Route path="/stakeholder-map" element={<StakeholderMapPage />} />
            <Route path="/email-signatures" element={<EmailSignatureManagerPage />} />
            <Route path="/business-continuity" element={<BusinessContinuityPage />} />
            <Route path="/compliance-evidence" element={<ComplianceEvidencePage />} />
            <Route path="/api-playground" element={<ApiPlaygroundPage />} />
            <Route path="/vendor-evaluation" element={<VendorEvaluationPage />} />
            <Route path="/system-announcements" element={<SystemAnnouncementsPage />} />
            {/* R37 */}
            <Route path="/benchmark-analytics" element={<BenchmarkAnalyticsPage />} />
            <Route path="/customer-retention" element={<CustomerRetentionPage />} />
            <Route path="/incident-postmortem" element={<IncidentPostmortemPage />} />
            <Route path="/resource-utilization" element={<ResourceUtilizationPage />} />
            <Route path="/client-renewal" element={<ClientRenewalPage />} />
            <Route path="/automation-log" element={<AutomationLogPage />} />
            <Route path="/payment-gateway" element={<PaymentGatewayPage />} />
            <Route path="/content-moderation" element={<ContentModerationPage />} />
            <Route path="/license-manager" element={<LicenseManagerPage />} />
            <Route path="/feedback-analytics" element={<FeedbackAnalyticsPage />} />
            {/* R38 */}
            <Route path="/profit-center" element={<ProfitCenterPage />} />
            <Route path="/sprint-velocity" element={<SprintVelocityPage />} />
            <Route path="/access-log" element={<AccessLogPage />} />
            <Route path="/raporty" element={<RaportyPage />} />
            <Route path="/nps-engine" element={<NpsEnginePage />} />
            <Route path="/email-drip" element={<EmailDripCampaignPage />} />
            <Route path="/department-budget" element={<DepartmentBudgetPage />} />
            <Route path="/qa-test-suite" element={<QaTestSuitePage />} />
            <Route path="/sales-pipeline" element={<SalesPipelinePage />} />
            <Route path="/data-backup-monitor" element={<DataBackupMonitorPage />} />
            <Route path="/client-survey-engine" element={<ClientSurveyEnginePage />} />
            {/* R39 */}
            <Route path="/tax-calculator" element={<TaxCalculatorPage />} />
            <Route path="/employee-handbook" element={<EmployeeHandbookPage />} />
            <Route path="/it-asset-discovery" element={<ItAssetDiscoveryPage />} />
            <Route path="/meeting-feedback" element={<MeetingFeedbackPage />} />
            <Route path="/compliance-training-log" element={<ComplianceTrainingLogPage />} />
            <Route path="/client-communication-hub" element={<ClientCommunicationHubPage />} />
            {/* R40 */}
            <Route path="/cash-flow" element={<CashFlowForecastPage />} />
            <Route path="/employee-survey" element={<EmployeeSurveyPage />} />
            <Route path="/lessons-learned" element={<LessonsLearnedPage />} />
            <Route path="/vendor-contracts" element={<VendorContractPage />} />
            <Route path="/db-health" element={<DatabaseHealthPage />} />
            <Route path="/client-referral" element={<ClientReferralPage />} />
            {/* R41 */}
            <Route path="/invoice-reconciliation" element={<InvoiceReconciliationPage />} />
            <Route path="/employee-timesheet" element={<EmployeeTimesheetPage />} />
            <Route path="/product-roadmap" element={<ProductRoadmapPage />} />
            <Route path="/service-metrics" element={<ServiceMetricsPage />} />
            <Route path="/customer-lifecycle" element={<CustomerLifecyclePage />} />
            <Route path="/vendor-payments" element={<VendorPaymentPage />} />
            <Route path="/document-classification" element={<DocumentClassificationPage />} />
            <Route path="/pnl" element={<ProfitLossReportPage />} />
            <Route path="/api-versions" element={<ApiVersionManagerPage />} />
            <Route path="/team-capacity" element={<TeamCapacityPage />} />
            {/* R42 MEGA */}
            <Route path="/ai-command" element={<AiCommandCenterPage />} />
            <Route path="/live-ops" element={<LiveOpsWallPage />} />
            <Route path="/strategic-planner" element={<StrategicPlannerPage />} />
            <Route path="/executive-briefing" element={<ExecutiveBriefingPage />} />
            <Route path="/smart-notifications" element={<SmartNotificationsEnginePage />} />
            <Route path="/process-mining" element={<ProcessMiningPage />} />
            <Route path="/business-rules" element={<BusinessRulesEnginePage />} />
            <Route path="/real-time-analytics" element={<RealTimeAnalyticsPage />} />
            <Route path="/platform-health" element={<PlatformHealthScorePage />} />

            {/* User Settings */}
            <Route path="/settings" element={<UserSettingsPage />} />

            {/* Legacy Routes - Redirects to CRM (inside protected layout) */}
            <Route path="/operator" element={<Navigate to="/crm" replace />} />
            <Route path="/operator/application/:id" element={<LegacyApplicationRedirect />} />
            <Route path="/applications" element={<LegacyApplicationsQueryRedirect />} />
            <Route path="/application/:id" element={<LegacyApplicationRedirect />} />
            <Route path="/pup" element={<Navigate to="/crm/apply" replace />} />
            <Route path="/candidate" element={<Navigate to="/crm/apply" replace />} />

            {/* Catch-all for sidebar items without dedicated pages — show "coming soon" instead of redirecting to login */}
            <Route path="*" element={<ComingSoonPage />} />
          </Route>

          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </Router>
    </ConfirmProvider>
    </ToastProvider>
    </CompanyProvider>
    </YearProvider>
  );
}

export default App;
